import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { UserProfile, Quest, LevelUpResult, Achievement, ShopItem, SubTask, DailyReward, Stats, Program, ProgramScheduleType, ProgramIntensity, ProgramDomain, DomainAnswers, SubscriptionTier, BackgroundSettings } from '../types';
import { userService } from '../services/userService';
import { questService } from '../services/questService';
import { achievementService } from '../services/achievementService';
import { soundService } from '../services/soundService';
import { shopService } from '../services/shopService';
import { statisticsService } from '../services/statisticsService';
import { rateLimitedFirestoreService as firestoreService } from '../services/rateLimitedFirestoreService';
import { firestoreSyncService } from '../services/firestoreSyncService';
import { authService } from '../services/authService';
import { getTitleForLevel } from '../utils/levelSystem';
import { dailyRewardService } from '../services/dailyRewardService';
import {
  trackEvent,
  trackSubscriptionActivated,
  trackTrialStarted,
  trackTrialExpired,
  trackSubscriptionCancelled,
  setAnalyticsUserProperties
} from '../services/analyticsService';
import { programService } from '../services/programService';
import { aiService } from '../services/aiService';
import { inputSanitizationService } from '../services/inputSanitizationService';
import { subscriptionService } from '../services/subscriptionService';
import { saveBackgroundSettings, getDefaultBackgroundSettings, cleanBackgroundSettings } from '../services/backgroundService';
import { programRecoveryService } from '../services/programRecoveryService';
import ProgramLoadingModal from '../components/ProgramLoadingModal';
import DemoBanner from '../components/DemoBanner';
import DemoConversionModal from '../components/DemoConversionModal';
import type { NotificationType } from '../components/SystemNotification';
import {
  validateInput,
  questCreateSchema,
  questUpdateSchema,
  programCreateSchema,
  checkRateLimit,
  rateLimitPresets,
  getRateLimitReset
} from '../utils/validation';

interface QuestCompleteData {
  quest: Quest;
  rewards: {
    xp: number;
    currency: number;
    stats: { [key: string]: number };
  };
}

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

interface UserContextType {
  profile: UserProfile | null;
  quests: Quest[];
  programs: Program[];
  levelUpQueue: LevelUpResult[];
  achievements: Achievement[];
  achievementQueue: Achievement[];
  shopItems: ShopItem[];
  user: User | null;
  questCompleteData: QuestCompleteData | null;
  clearQuestComplete: () => void;
  notifications: Notification[];
  showNotification: (message: string, type: NotificationType) => void;
  removeNotification: (id: string) => void;
  showPenaltyModal: boolean;
  failedDailyQuests: Quest[];
  acceptPenalty: () => void;
  dailyReward: DailyReward | null;
  showDailyRewardModal: boolean;
  claimDailyReward: () => void;
  allocateStatPoints: (statAllocations: Partial<Stats>) => void;
  refreshProfile: () => void;
  refreshQuests: () => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  completeQuest: (questId: string) => void;
  createQuest: (title: string, description: string, type: any, difficulty: any, resetDaily?: boolean, scheduledFor?: string, subTasks?: SubTask[], scheduledTime?: { start: string; end: string }, allDay?: boolean) => void;
  createQuestsBatch: (questsData: Array<{title: string; description: string; type: any; difficulty: any; resetDaily?: boolean; scheduledFor?: string; subTasks?: SubTask[]; programId?: string; resources?: string[]; scheduledTime?: { start: string; end: string }; allDay?: boolean;}>, bypassRateLimit?: boolean) => Promise<void>;
  updateQuest: (questId: string, updates: Partial<Quest>, silent?: boolean) => void;
  toggleSubTask: (questId: string, subTaskId: string) => void;
  deleteQuest: (questId: string, silent?: boolean, deleteSeries?: boolean) => void;
  rescheduleQuest: (questId: string, newDate: string) => void;
  clearLevelUpQueue: () => void;
  setLevelUpQueue: React.Dispatch<React.SetStateAction<LevelUpResult[]>>;
  clearAchievementQueue: () => void;
  purchaseItem: (itemId: string) => boolean;
  completePomodoroSession: (focusMinutes: number) => void;
  createProgram: (goal: string, duration: number, scheduleType: ProgramScheduleType, customDays: number[] | undefined, questsPerDay: number, intensity: ProgramIntensity, domain: ProgramDomain, domainAnswers: DomainAnswers, aiFollowUpAnswers?: { [key: string]: string }, preferredTimeStart?: string, preferredTimeEnd?: string, availableDays?: number[], skillLevel?: 'beginner' | 'intermediate' | 'advanced', timePerDay?: number, learningStyle?: string, specificFocus?: string, availableResources?: string) => Promise<string>;
  pauseProgram: (programId: string) => Promise<void>;
  resumeProgram: (programId: string) => Promise<void>;
  deleteProgram: (programId: string) => Promise<void>;
  getEffectiveStats: () => Stats; // Get stats with passive boosts applied
  // ⚡ OPTIMIZATION: Passive skills in context
  passiveSkills: any[];
  updatePassiveSkillInContext: (skillId: string, updates: any) => void;
  addPassiveSkillToContext: (skill: any) => void;
  removePassiveSkillFromContext: (skillId: string) => void;
  updateBackgroundSettings: (settings: BackgroundSettings) => Promise<void>;
  getBackgroundSettings: () => BackgroundSettings;
  signOut: () => void;
  isDemoMode: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode; user?: User }> = ({ children, user }) => {
  // CRITICAL FIX: Don't initialize with localStorage - wait for Firestore
  const [profile, setProfile] = useState<UserProfile | null>(null);
  
  const [quests, setQuests] = useState<Quest[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [levelUpQueue, setLevelUpQueue] = useState<LevelUpResult[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [achievementQueue, setAchievementQueue] = useState<Achievement[]>([]);
  const [shopItems] = useState<ShopItem[]>(shopService.getShopItems());
  const [dataLoaded, setDataLoaded] = useState(false);
  const [profileCreationInProgress, setProfileCreationInProgress] = useState(false);
  // ⚡ OPTIMIZATION: Add passive skills to context to eliminate separate loading
  const [passiveSkills, setPassiveSkills] = useState<any[]>([]);
  const [questCompleteData, setQuestCompleteData] = useState<QuestCompleteData | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [failedDailyQuests, setFailedDailyQuests] = useState<Quest[]>([]);
  const [dailyReward, setDailyReward] = useState<DailyReward | null>(null);
  const [showDailyRewardModal, setShowDailyRewardModal] = useState(false);
  const [dailyRewardShownToday, setDailyRewardShownToday] = useState(false);
  const [showProgramLoading, setShowProgramLoading] = useState(false);
  const [currentProgramDuration, setCurrentProgramDuration] = useState(30);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showDemoConversion, setShowDemoConversion] = useState(false);
  
  // 🔒 LOCK: Prevent duplicate progressive quest generation
  const generatingPhaseRef = useRef<Set<string>>(new Set()); // Store "programId-phase" keys

  // Helper: Consolidated error handling (SIMPLIFIED)
  const handleError = (error: any, context: string, silent: boolean = false) => {
    const errorMessage = error?.message || 'An error occurred';
    console.error(`${context}:`, error);
    if (!silent) {
      showNotification(`Error: ${errorMessage}`, 'warning');
    }
  };

  useEffect(() => {
    // Preload sounds for better performance
    soundService.preloadSounds();
    
    // Check for demo mode
    const isDemoMode = sessionStorage.getItem('is_demo_mode') === 'true';
    if (isDemoMode) {
      setIsDemoMode(true);
      const demoUserStr = sessionStorage.getItem('demo_user');
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr);
        setProfile(demoUser.profile);
        setQuests(demoUser.quests);
        setPassiveSkills(demoUser.passiveSkills || []);
        
        // Load achievements from demo user data or use initial achievements
        const demoAchievements = demoUser.achievements || achievementService.getInitialAchievements();
        setAchievements(demoAchievements);
        
        // Track demo session start (if not already tracked)
        const sessionStartTime = sessionStorage.getItem('demo_session_start_time');
        if (!sessionStartTime) {
          sessionStorage.setItem('demo_session_start_time', Date.now().toString());
          trackEvent('demo_session_started', {
            demo_user_level: demoUser.profile.level,
            demo_quests_count: demoUser.quests.length,
            demo_session_id: demoUser.id
          });
        }
        
        setDataLoaded(true);
        return; // Skip normal user loading
      }
    }

    // If no user and not in demo mode, return early
    if (!user && !isDemoMode) {
      setDataLoaded(true);
      return;
    }

    // If user is authenticated, clear any demo mode state
    if (user) {
      // Clear demo mode state completely
      const isDemoMode = sessionStorage.getItem('is_demo_mode') === 'true';
      if (isDemoMode) {
        sessionStorage.removeItem('is_demo_mode');
        sessionStorage.removeItem('demo_user');
        sessionStorage.removeItem('demo_session_start_time');
        localStorage.removeItem('demo_background_settings');
        setIsDemoMode(false);
        
        // Track demo to authenticated conversion
        trackEvent('demo_to_authenticated_conversion', {
          conversion_type: 'demo_to_auth',
          user_authenticated: true
        });
        
        // 🔧 AUTO-REFRESH: Force page refresh to clear demo mode completely
        setTimeout(() => {
          window.location.reload();
        }, 100); // Small delay to ensure state is cleared
      }
    }
    
    // ⚡ OPTIMIZED: Load user data with parallel loading and progressive rendering
    const loadUserData = async () => {
      try {
        // ⚡ ULTRA-OPTIMIZED: Load ONLY critical data first (profile + quests)
        // Everything else loads in background after UI is shown
        if (!user) {
          return;
        }
        
        // Ensure user is properly authenticated before proceeding
        if (!user.uid) {
          return;
        }
        
        // Wait for authentication to be fully established
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const [firestoreProfile, firestoreQuests] = await Promise.all([
          firestoreService.getUserProfile(user.uid),
          firestoreService.getUserQuests(user.uid, {
            includeCompleted: true,  // ✅ Include completed quests to prevent data loss
            limit: 100               // ✅ Increased limit to include more quests
          }),
        ]);
        
        // ⚡ BACKGROUND LOADING: Load secondary data after UI is shown
        const backgroundLoadPromise = (async () => {
          try {
            if (!user) return { programs: [], stats: [], achievements: [], passiveSkills: [] };
            
            const [programs, stats, achievements, passiveSkills] = await Promise.all([
              firestoreService.getUserPrograms(user.uid),
              firestoreService.getUserStatistics(user.uid, {
                daysAgo: 90,
                limit: 90
              }),
              firestoreService.getAchievements(user.uid),
              firestoreService.getUserPassiveSkills(user.uid)
            ]);
            return { programs, stats, achievements, passiveSkills };
          } catch (error) {
            console.warn('Background data load failed:', error);
            return { programs: [], stats: [], achievements: [], passiveSkills: [] };
          }
        })();
        
        // ⚡ DEFERRED: Ban check runs completely in background
        setTimeout(async () => {
          try {
            const { httpsCallable } = await import('firebase/functions');
            const { functions } = await import('../firebase');
            const checkBanStatus = httpsCallable(functions, 'checkBanStatus');
            const banResult = await checkBanStatus({});
            
            if (banResult.data && typeof banResult.data === 'object' && 'success' in banResult.data) {
              const banData = banResult.data as any;
              if (banData.success && banData.data && banData.data.isBanned) {
                let banMessage = 'Your account has been suspended.\n\n';
                if (banData.data.isPermanent) {
                  banMessage += 'Suspension Type: Permanent\n\n';
                } else if (banData.data.bannedUntil) {
                  banMessage += 'Suspended Until: ' + new Date(banData.data.bannedUntil).toLocaleString() + '\n\n';
                }
                banMessage += 'Reason: ' + (banData.data.reason || 'Violation of terms of service');
                alert(banMessage);
                await authService.signOut();
              }
            }
          } catch (error) {
            console.warn('Ban check failed:', error);
          }
        }, 2000); // Check after 2 seconds
        
        if (firestoreProfile) {
          // Existing user - set critical data immediately
          
          // ⚡ DEFERRED: Check subscription expiration in background (non-blocking)
          setTimeout(async () => {
            const subscription = firestoreProfile.subscription;
            if (!subscription) return;
            
            const now = Date.now();
            let shouldCheckExpiration = false;
            let isTrialExpiration = false;
            
            // Check trial expiration
            if (subscription.tier === 'trial' && subscription.trialEndsAt) {
              if (now > subscription.trialEndsAt) {
                shouldCheckExpiration = true;
                isTrialExpiration = true;
              }
            }
            
            // Check paid subscription expiration (E-Rank, S-Rank)
            if ((subscription.tier === 'e_rank' || subscription.tier === 's_rank') && subscription.currentPeriodEnd) {
              if (now > subscription.currentPeriodEnd) {
                shouldCheckExpiration = true;
                isTrialExpiration = false;
              }
            }
            
            if (shouldCheckExpiration) {
              console.log(`⏰ Checking subscription (${subscription.tier}) expiration...`);

              try {
                // Double-check authentication before calling cloud function
                const { auth } = await import('../firebase');

                if (!auth.currentUser) {
                  throw new Error('User not authenticated');
                }

                // Ensure auth token is fresh
                try {
                  await auth.currentUser.getIdToken(true); // Force refresh token
                } catch (tokenError) {
                  throw new Error('Authentication token refresh failed');
                }

                // Final check before calling cloud function
                if (!auth.currentUser) {
                  throw new Error('User authentication lost');
                }

                // Use pre-initialized functions instance for better auth handling
                const { httpsCallable } = await import('firebase/functions');
                const { functions } = await import('../firebase');
                const checkTrialExpiration = httpsCallable(functions, 'checkTrialExpiration');

                console.log('🔍 Calling checkTrialExpiration cloud function...');
                const result = await checkTrialExpiration();
                const data = result.data as any;
                console.log('✅ Cloud function response:', data);

                if (data.expired) {
                  console.log(`✅ Subscription (${subscription.tier}) confirmed expired`);
                  const expiredTier = subscription.tier;

                  // Update profile with expired state
                  setProfile(prev => prev ? {
                    ...prev,
                    subscription: {
                      ...prev.subscription,
                      tier: 'expired',
                      status: 'expired',
                      previousTier: expiredTier,
                      updatedAt: now,
                    }
                  } : prev);

                  // Analytics
                  if (isTrialExpiration) {
                    const trialStarted = subscription.createdAt || now;
                    const daysUsed = Math.floor((now - trialStarted) / (1000 * 60 * 60 * 24));
                    trackTrialExpired(daysUsed, false);
                  }

                  // Show notification
                  const message = isTrialExpiration
                    ? '⏰ Your 14-day trial has expired. Subscribe to continue using premium features!'
                    : '⏰ Your subscription has expired. Please renew to continue using premium features!';
                  showNotification(message, 'warning');
                }
              } catch (error) {
                console.error('Error checking subscription expiration:', error);

                // 🛡️ FALLBACK: Check expiration locally if cloud function fails
                // This ensures users with expired trials can still login and see the expired modal
                console.log('🔄 Cloud function failed, checking expiration locally...');

                let isExpired = false;
                if (isTrialExpiration && subscription.trialEndsAt && now > subscription.trialEndsAt) {
                  isExpired = true;
                  console.log('⏰ Trial expired (local check)');
                } else if (!isTrialExpiration && subscription.currentPeriodEnd && now > subscription.currentPeriodEnd) {
                  isExpired = true;
                  console.log('⏰ Paid subscription expired (local check)');
                }

                if (isExpired) {
                  const expiredTier = subscription.tier;

                  // Update profile with expired state
                  setProfile(prev => prev ? {
                    ...prev,
                    subscription: {
                      ...prev.subscription,
                      tier: 'expired',
                      status: 'expired',
                      previousTier: expiredTier,
                      updatedAt: now,
                    }
                  } : prev);

                  // Analytics
                  if (isTrialExpiration) {
                    const trialStarted = subscription.createdAt || now;
                    const daysUsed = Math.floor((now - trialStarted) / (1000 * 60 * 60 * 24));
                    trackTrialExpired(daysUsed, false);
                  }

                  // Show notification
                  const message = isTrialExpiration
                    ? '⏰ Your 14-day trial has expired. Subscribe to continue using premium features!'
                    : '⏰ Your subscription has expired. Please renew to continue using premium features!';
                  showNotification(message, 'warning');
                }
              }
            }
          }, 3000); // Check after 3 seconds to allow auth to fully establish
          
          // 🔧 BACKWARD COMPATIBILITY: Add subscription and aiUsage if missing
          let profileNeedsSaving = false;
          
          if (!firestoreProfile.subscription) {
            firestoreProfile.subscription = subscriptionService.createTrialSubscription();
            profileNeedsSaving = true;
          }
          if (!firestoreProfile.aiUsage) {
            // Empty object - will be populated with month keys as AI features are used
            firestoreProfile.aiUsage = {};
            profileNeedsSaving = true;
          }
          
          // 🐛 FIX: Save the profile to Firestore if we added subscription/aiUsage
          // Otherwise, the trial will reset every time the user refreshes!
          if (profileNeedsSaving) {
            firestoreService.saveUserProfile(user.uid, firestoreProfile).catch(error => {
              console.error('Error saving profile after adding subscription:', error);
            });
          }
          
          setProfile(firestoreProfile);
          setQuests(firestoreQuests);
          
          // ⚡ UI CAN SHOW NOW - User sees dashboard immediately!
          setDataLoaded(true);
          
          // ⚡ BACKGROUND: Load and set secondary data (non-blocking)
          backgroundLoadPromise.then(({ programs, stats, achievements: firestoreAchievements, passiveSkills }) => {
            // Set programs and passive skills
            setPrograms(programs || []);
            setPassiveSkills(passiveSkills || []);
            statisticsService.loadStatistics(stats || []);
            
            // Handle achievements
            if (firestoreAchievements && firestoreAchievements.length > 0) {
              const mergedAchievements = achievementService.mergeAchievements(firestoreAchievements);
              setAchievements(mergedAchievements);
              
              // Save in background if new achievements added (non-blocking)
              if (mergedAchievements.length > firestoreAchievements.length) {
                firestoreService.saveAchievements(user.uid, mergedAchievements).catch(console.error);
              }
            } else {
              const initialAchievements = achievementService.getInitialAchievements();
              setAchievements(initialAchievements);
              // Save in background (non-blocking)
              firestoreService.saveAchievements(user.uid, initialAchievements).catch(console.error);
            }
            
            // 🛡️ FAIL-SAFE: Check for incomplete program generations and attempt recovery
            if (programs && programs.length > 0) {
              setTimeout(() => {
                programRecoveryService.checkAndRecoverPrograms(
                  user.uid,
                  programs,
                  firestoreQuests,
                  (program, phase) => {
                    // Use a wrapper function that doesn't require the full context
                    return checkAndGenerateNextPhase(program);
                  }
                ).catch(error => {
                  console.error('Program recovery check failed:', error);
                });
              }, 5000); // Wait 5 seconds after app load to avoid blocking UI
            }
          }).catch(error => {
            console.error('Error loading background data:', error);
            // Set empty defaults on error
            setPrograms([]);
            setPassiveSkills([]);
            setAchievements(achievementService.getInitialAchievements());
          });
          
          // 🚀 OPTIMIZATION: Process daily quest resets in background (non-blocking)
          setTimeout(() => {
            const today = new Date();
            const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            
            const resetQuests = firestoreQuests.map(quest => {
              if (quest.resetDaily && quest.status === 'completed' && quest.completedAt) {
                const completedDate = new Date(quest.completedAt);
                const completedDateOnly = new Date(
                  completedDate.getFullYear(),
                  completedDate.getMonth(),
                  completedDate.getDate()
                );
                
                // Reset if completed on a different day
                if (completedDateOnly.getTime() !== todayDateOnly.getTime()) {
                  return {
                    ...quest,
                    status: 'active' as const,
                    completedAt: null,
                    // Reset subtasks if they exist
                    subTasks: quest.subTasks?.map(st => ({ ...st, completed: false }))
                  };
                }
              }
              return quest;
            });
            
            // Only update if quests were actually reset
            const questsWereReset = resetQuests.some((q, i) => 
              q.status !== firestoreQuests[i]?.status || 
              q.completedAt !== firestoreQuests[i]?.completedAt
            );
            
            if (questsWereReset) {
              setQuests(resetQuests);
              Promise.all(
                resetQuests
                  .filter((q, i) => q.status !== firestoreQuests[i]?.status)
                  .map(q => firestoreService.saveQuest(user.uid, q))
              ).catch(error => {
                console.error('⚠️ Error saving reset quests:', error);
              });
            }
          }, 0); // Execute after UI renders
        } else {
          // New user - create fresh profile in Firestore
          
          // ✅ GUARD: Prevent duplicate creation if useEffect runs multiple times
          const questCreationKey = `quests_created_${user.uid}`;
          if (sessionStorage.getItem(questCreationKey)) {
            setDataLoaded(true);
            return;
          }
          
          // Mark that we're creating quests
          sessionStorage.setItem(questCreationKey, 'true');
          
          // Get signup data if available
          const signupDataKey = `signup_data_${user.uid}`;
          const signupDataStr = localStorage.getItem(signupDataKey);
          let signupData = null;
          if (signupDataStr) {
            try {
              signupData = JSON.parse(signupDataStr);
              // Clean up the temporary data
              localStorage.removeItem(signupDataKey);
            } catch (error) {
              handleError(error, 'Failed to parse signup data', true);
            }
          }
          
          // Clear any demo mode data when user signs up
          if (isDemoMode) {
            sessionStorage.removeItem('is_demo_mode');
            sessionStorage.removeItem('demo_user');
            sessionStorage.removeItem('demo_user_id');
            localStorage.removeItem('demo_background_settings');
            setIsDemoMode(false);
          }

          const newProfile: UserProfile = {
            id: user.uid,
            name: user.displayName || user.email?.split('@')[0] || 'Hunter',
            dateOfBirth: signupData?.dateOfBirth || null,
            level: 1,
            currentXP: 0,
            xpToNextLevel: 100,
            totalXP: 0,
            currentTitle: 'Civilian',
            stats: {
              strength: 10,
              intelligence: 10,
              agility: 10,
              vitality: 10,
              sense: 10,
            },
            passiveStatBoosts: {
              strength: 0,
              intelligence: 0,
              agility: 0,
              vitality: 0,
              sense: 0,
            },
            unallocatedStatPoints: 0,
            createdAt: Date.now(),
            streak: 0,
            lastActivityDate: new Date().toISOString().split('T')[0],
            lastLoginDate: '',
            lastPenaltyCheck: '',
            longestStreak: 0,
            currency: 100,
            inventory: [],
            pomodoroSessions: 0,
            totalFocusTime: 0,
            penaltyActive: false,
            penaltyQuestIds: [],
            // These fields are created by server-side functions but included here for TypeScript
            subscription: subscriptionService.createTrialSubscription(),
            aiUsage: {},
            lifetimeStats: {
              totalQuestsCompleted: 0,
              totalXPEarned: 0,
              totalCurrencyEarned: 0,
              questsByDifficulty: {
                easy: 0,
                normal: 0,
                hard: 0,
                boss: 0,
              },
              questsByType: {
                daily: 0,
                main: 0,
                instant: 0,
                boss: 0,
              },
            },
            legalAgreements: signupData?.legalAgreements || null,
          };
          
          // Set profile creation in progress
          setProfileCreationInProgress(true);
          console.log('🔄 Starting profile creation for new user:', user.uid);
          
          // Save to Firestore with retry logic
          let saveAttempts = 0;
          const maxAttempts = 3;
          let saveSuccess = false;
          
          while (saveAttempts < maxAttempts && !saveSuccess) {
            try {
              await firestoreService.saveUserProfile(user.uid, newProfile);
              saveSuccess = true;
              console.log('✅ User profile saved to Firestore successfully');
            } catch (saveError) {
              saveAttempts++;
              console.error(`❌ Failed to save user profile (attempt ${saveAttempts}/${maxAttempts}):`, saveError);
              
              // Check if it's a network error
              const error = saveError as any;
              const isNetworkError = error.code === 'unavailable' || 
                                   error.message?.includes('network') ||
                                   error.message?.includes('timeout') ||
                                   !navigator.onLine;
              
              if (isNetworkError) {
                console.warn('🌐 Network error detected, will retry...');
              }
              
              if (saveAttempts >= maxAttempts) {
                // Final attempt failed - show error and sign out user
                console.error('🚨 CRITICAL: Failed to save user profile after all retries');
                setProfileCreationInProgress(false);
                alert('Failed to create your profile. Please try signing in again.');
                await authService.signOut();
                return;
              }
              
              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * saveAttempts));
            }
          }
          
          setProfile(newProfile);
          setProfileCreationInProgress(false);
          
          // 📊 ANALYTICS: Track trial started for new user
          trackTrialStarted('signup');
          setAnalyticsUserProperties({
            subscription_tier: 'trial',
            subscription_status: 'trialing',
            is_new_user: true
          });
          
          // Create sample quests for new user (one-time, don't reset daily)
          const defaultQuests: Quest[] = [
            {
              id: `quest_${Date.now()}_1`,
              userId: user.uid,
              title: 'Read for 30 Minutes',
              description: 'Spend 30 minutes reading a book or educational content',
              type: 'daily',
              difficulty: 'easy',
              xpReward: 15,
              currencyReward: 2,
              statRewards: { intelligence: 1, sense: 1 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false, // Sample quests don't reset daily
            },
            {
              id: `quest_${Date.now()}_2`,
              userId: user.uid,
              title: 'Social Connection',
              description: 'Reach out to a friend or family member for a meaningful conversation',
              type: 'daily',
              difficulty: 'easy',
              xpReward: 20,
              currencyReward: 3,
              statRewards: { sense: 2 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false, // Sample quests don't reset daily
            },
            {
              id: `quest_${Date.now()}_3`,
              userId: user.uid,
              title: 'Deep Work Session',
              description: 'Focus on a complex task for 2 hours without distractions',
              type: 'main',
              difficulty: 'hard',
              xpReward: 60,
              currencyReward: 8,
              statRewards: { intelligence: 3, agility: 2 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false,
            },
            {
              id: `quest_${Date.now()}_4`,
              userId: user.uid,
              title: 'Meditation Practice',
              description: 'Complete a 20-minute meditation session',
              type: 'daily',
              difficulty: 'easy',
              xpReward: 18,
              currencyReward: 2,
              statRewards: { sense: 1, vitality: 1 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false, // Sample quests don't reset daily
            },
            {
              id: `quest_${Date.now()}_5`,
              userId: user.uid,
              title: 'Skill Development',
              description: 'Practice a musical instrument or creative skill for 45 minutes',
              type: 'daily',
              difficulty: 'normal',
              xpReward: 35,
              currencyReward: 4,
              statRewards: { sense: 2, intelligence: 1 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false, // Sample quests don't reset daily
            },
            {
              id: `quest_${Date.now()}_6`,
              userId: user.uid,
              title: 'Boss Quest: Personal Project',
              description: 'Complete a significant milestone in your personal project',
              type: 'boss',
              difficulty: 'boss',
              xpReward: 100,
              currencyReward: 15,
              statRewards: { strength: 2, intelligence: 3, agility: 2, vitality: 2, sense: 2 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false,
            },
            {
              id: `quest_${Date.now()}_7`,
              userId: user.uid,
              title: 'Evening Reflection',
              description: 'Spend 15 minutes reflecting on your day and planning tomorrow',
              type: 'daily',
              difficulty: 'easy',
              xpReward: 12,
              currencyReward: 1,
              statRewards: { intelligence: 1 },
              status: 'active',
              createdAt: Date.now(),
              completedAt: null,
              resetDaily: false, // Sample quests don't reset daily
            },
          ];
          
          // Save to Firestore
          await firestoreService.saveQuests(user.uid, defaultQuests);
          setQuests(defaultQuests);
          
          // Initialize achievements for new user
          const initialAchievements = achievementService.getInitialAchievements();
          await firestoreService.saveAchievements(user.uid, initialAchievements);
          setAchievements(initialAchievements);
        }
        
        setDataLoaded(true);
      } catch (error) {
        console.error('❌ Error loading user data:', error);
        setDataLoaded(true);
      }
    };

    loadUserData();
    
    // 🔒 SECURITY: Periodic ban check (every 30 minutes while app is open)
    // ✅ OPTIMIZED: Reduced frequency + added caching (83% cost reduction vs 5 min)
    // Balances cost-efficiency with timely ban enforcement
    const BAN_CHECK_INTERVAL = 1800000; // 30 minutes
    const BAN_CHECK_CACHE_KEY = 'last_ban_check';
    
    const banCheckInterval = setInterval(async () => {
      try {
        // ✅ Check cache to prevent redundant checks
        const lastCheck = sessionStorage.getItem(BAN_CHECK_CACHE_KEY);
        const now = Date.now();
        
        if (lastCheck && (now - parseInt(lastCheck)) < BAN_CHECK_INTERVAL) {
          console.log('⏭️ Skipping ban check (cached)');
          return;
        }
        
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../firebase');
        const checkBanStatus = httpsCallable(functions, 'checkBanStatus');
        const banResult = await checkBanStatus({});
        
        // Cache the check timestamp
        sessionStorage.setItem(BAN_CHECK_CACHE_KEY, now.toString());
        
        if (banResult.data && typeof banResult.data === 'object' && 'success' in banResult.data) {
          const banData = banResult.data as any;
          if (banData.success && banData.data && banData.data.isBanned) {
            // User was just banned - sign them out
            console.log('🚫 User was banned during session. Signing out...');
            
            let banMessage = 'Your account has been suspended.\n\n';
            if (banData.data.isPermanent) {
              banMessage += 'Suspension Type: Permanent\n\n';
            } else if (banData.data.bannedUntil) {
              const remainingTime = new Date(banData.data.bannedUntil).toLocaleString();
              banMessage += 'Suspended Until: ' + remainingTime + '\n\n';
            }
            banMessage += 'Reason: ' + (banData.data.reason || 'Violation of terms of service') + '\n\n';
            banMessage += 'If you believe this is an error, please contact support.';
            
            alert(banMessage);
            
            // Clear cache on sign out
            sessionStorage.removeItem(BAN_CHECK_CACHE_KEY);
            await authService.signOut();
          }
        }
      } catch (error) {
        // Silently fail - don't interrupt user experience
        console.error('Error in periodic ban check:', error);
      }
    }, BAN_CHECK_INTERVAL); // Check every 30 minutes (6x reduction from 5 min)
    
    // Cleanup interval on unmount
    return () => {
      clearInterval(banCheckInterval);
      sessionStorage.removeItem(BAN_CHECK_CACHE_KEY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync pending changes before page close or unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      firestoreSyncService.forceSync();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Force sync on unmount
      firestoreSyncService.forceSync();
    };
  }, []);

  // Check for failed daily quests at midnight
  useEffect(() => {
    if (!profile || !dataLoaded) return;

    // Skip penalty checks in demo mode
    if (isDemoMode) return;

    const checkDailyQuests = () => {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      
      // Check if there's a penalty already checked today
      // (prevent duplicate penalties on same day)
      if (profile.lastPenaltyCheck === today) {
        // Already checked today, skip
        return;
      }
      
      // Find all incomplete quests that are overdue
      // and are still active (not completed)
      const failedDailies = quests.filter(quest => {
        if (quest.status !== 'active') return false;
        
        // SKIP daily reset quests - they auto-reset every day and shouldn't trigger penalties
        // (They manage their own lifecycle via resetDaily logic in questService.ts)
        if (quest.resetDaily) {
          return false;
        }
        
        // Check if it's a scheduled quest that's past its due date
        if (quest.scheduledFor) {
          // Quest was scheduled for yesterday or earlier and is still incomplete
          return quest.scheduledFor < today;
        }
        
        // Check if it's a regular quest created before today and still incomplete
        // Any quest not completed by the next day triggers a penalty
        if (!quest.resetDaily && !quest.scheduledFor) {
          const questCreatedDate = new Date(quest.createdAt).toISOString().split('T')[0];
          // Quest was created before today and is still incomplete
          return questCreatedDate < today;
        }
        
        return false;
      });
      
      if (failedDailies.length > 0) {
        // Mark that we've checked today in Firestore
        const updatedProfile = {
          ...profile,
          lastPenaltyCheck: today,
        };
        setProfile(updatedProfile);
        if (user) {
          firestoreSyncService.syncProfile(user.uid, updatedProfile);
        }
        
        // Activate penalty
        setFailedDailyQuests(failedDailies);
        setShowPenaltyModal(true);
      } else if (profile.lastPenaltyCheck !== today) {
        // No failed quests, but it's a new day - mark as checked
        const updatedProfile = {
          ...profile,
          lastPenaltyCheck: today,
        };
        setProfile(updatedProfile);
        if (user) {
          firestoreSyncService.syncProfile(user.uid, updatedProfile);
        }
      }
    };

    // Check immediately on load
    checkDailyQuests();

    // Check every minute for midnight rollover
    const interval = setInterval(checkDailyQuests, 60000);

    return () => clearInterval(interval);
  }, [profile, quests, dataLoaded, isDemoMode]);

  // Check for daily login reward
  useEffect(() => {
    if (!profile || !dataLoaded) return;

    const checkDailyReward = () => {
      // Check localStorage to prevent duplicate rewards on page refresh
      const today = new Date().toISOString().split('T')[0];
      const lastRewardDate = localStorage.getItem('dailyRewardShownDate');
      const lastRewardUserId = localStorage.getItem('dailyRewardUserId');
      
      // Don't show daily reward if it's already been shown today for this user
      if (dailyRewardShownToday || (lastRewardDate === today && lastRewardUserId === profile.id)) {
        return;
      }

      const reward = dailyRewardService.generateDailyReward(
        profile.lastLoginDate,
        profile.streak
      );

      if (reward) {
        setDailyReward(reward);
        setShowDailyRewardModal(true);
        setDailyRewardShownToday(true); // Mark as shown today
        
        // Immediately reflect new streak and lastLoginDate in UI to avoid regress on refresh
        const updatedForStreak = {
          ...profile,
          streak: reward.streakDay,
          longestStreak: Math.max(reward.streakDay, profile.longestStreak),
          lastLoginDate: dailyRewardService.getTodayDateString(),
        } as UserProfile;
        setProfile(updatedForStreak);
        if (user) {
          firestoreSyncService.syncProfile(user.uid, updatedForStreak);
        }
        
        // Store in localStorage to prevent duplicates on refresh
        localStorage.setItem('dailyRewardShownDate', today);
        localStorage.setItem('dailyRewardUserId', profile.id);
        
        // Removed misleading quest completion sound
      }
    };

    // Check for daily reward on load
    checkDailyReward();
  }, [profile?.lastLoginDate, dataLoaded, dailyRewardShownToday]); // 🐛 FIX: Only check when lastLoginDate changes, not on every profile update

  // Reset daily reward shown flag when a new day starts
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastShownDate = localStorage.getItem('dailyRewardShownDate');
    
    if (lastShownDate !== today) {
      setDailyRewardShownToday(false);
      localStorage.setItem('dailyRewardShownDate', today);
      // Clear user ID to allow new day rewards
      localStorage.removeItem('dailyRewardUserId');
    }
  }, []);

  // Update program progress and check milestones (debounced)
  // ✅ OPTIMIZED: Debounced updates + only trigger on quest completions
  useEffect(() => {
    if (!profile || !dataLoaded || programs.length === 0) return;

    // ✅ Debounce to prevent rapid updates on quest completions
    const timeoutId = setTimeout(async () => {
      const activePrograms = programs.filter(p => p.status === 'active');
      if (activePrograms.length === 0) return;

      // Collect all programs that need updates
      const programsToUpdate: Program[] = [];

      for (const program of activePrograms) {
        // Use universal phase detection that handles both AI and traditional programs
        const currentPhase = programService.getCurrentPhaseUniversal(program, quests);
        const progress = programService.calculateProgress(program, quests);
        const daysRemaining = programService.getDaysRemaining(program);

        // ✅ Only update if there's a meaningful change (1% threshold for progress)
        const progressChanged = Math.abs(progress - program.progress) > 1;
        const phaseChanged = currentPhase !== program.currentPhase;
        
        if (!progressChanged && !phaseChanged && daysRemaining > 0) {
          continue; // Skip update if nothing changed
        }

        // Check for completed milestones with fresh quest data
        const completedMilestones = programService.checkMilestones(program, () => quests);
        
        // Check if program should be completed
        if (daysRemaining === 0 && progress >= 100) {
          const completedProgram = programService.completeProgram(program);
          programsToUpdate.push(completedProgram);
          showNotification(`🎊 Program completed: ${program.title}!`, 'achievement');
          soundService.achievementUnlocked();
        } else if (phaseChanged || progressChanged || completedMilestones.length > 0) {
          const updatedProgram = {
            ...program,
            currentPhase,
            progress,
            milestones: program.milestones,
          };
          programsToUpdate.push(updatedProgram);

          // Show milestone notification
          if (completedMilestones.length > 0) {
            completedMilestones.forEach(milestone => {
              showNotification(`🏆 Milestone reached: ${milestone.title}!`, 'achievement');
            });
          }
        }
      }

      // ✅ Batch update all programs at once
      if (programsToUpdate.length > 0) {
        setPrograms(prev => 
          prev.map(p => programsToUpdate.find(u => u.id === p.id) || p)
        );
        
        // Batch save to Firestore (use updatePrograms to avoid rate limits)
        if (user) {
          await firestoreService.updatePrograms(user.uid, programsToUpdate);
        }
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(timeoutId);
  }, [
    // ✅ Only trigger when quest completion count changes, not on every profile update
    programs.map(p => `${p.id}_${p.questsCompleted}`).join(','),
    dataLoaded,
    user?.uid
  ]);

  // 💳 SUBSCRIPTION SYNC: Handled entirely by Cloud Function trigger
  // ✅ OPTIMIZATION: Removed real-time listener (was causing 150K-600K reads/month)
  // 
  // The server-side syncSubscriptionToUser function (functions/src/subscription/syncSubscriptionToUser.ts)
  // automatically updates the user profile when subscription changes occur from Lemon Squeezy webhooks.
  // 
  // Benefits of removal:
  // - No redundant Firestore reads on every user document change
  // - Subscription data is always fresh on page load
  // - Server-side sync ensures data consistency
  // - Cost reduction: 150K-600K reads/month per 100 users ($0.30-$1.20/month saved)
  //
  // If real-time subscription notifications are needed in the future, consider:
  // 1. Using Cloud Messaging (push notifications)
  // 2. Polling with long intervals (5+ minutes) with caching
  // 3. WebSocket-based updates from Cloud Functions

  const claimDailyReward = () => {
    if (!profile || !dailyReward) return;

    // Apply rewards (without level up processing yet)
    const { newXP, newCurrency, newStats, statPoints } = dailyRewardService.applyReward(
      dailyReward,
      profile.currentXP,
      profile.currency,
      profile.stats
    );

    // Process level ups from XP gain
    let currentXP = newXP;
    let newLevel = profile.level;
    let xpToNext = profile.xpToNextLevel;
    const levelUpResults: LevelUpResult[] = [];
    let statPointsFromLevelUp = 0;

    // Calculate level ups (same logic as completeQuest)
    while (currentXP >= xpToNext) {
      currentXP -= xpToNext;
      newLevel++;
      
      // Give 3 stat points per level up for manual allocation
      statPointsFromLevelUp += 3;
      
      levelUpResults.push({
        newLevel,
        statIncreases: {}, // No auto stat allocation - player chooses
        newTitle: getTitleForLevel(newLevel)
      });
      
      xpToNext = Math.floor(100 * Math.pow(1.1, newLevel - 1));
    }

    // Total stat points = from rewards + from level ups
    const totalStatPoints = statPoints + statPointsFromLevelUp;

    // Update profile with rewards, level ups, and new login date
    const updatedProfile: UserProfile = {
      ...profile,
      level: newLevel,
      currentXP: currentXP,
      xpToNextLevel: xpToNext,
      totalXP: profile.totalXP + dailyReward.xp + (dailyReward.bonusReward?.type === 'xp_boost' ? (dailyReward.bonusReward.value as number) : 0) + (dailyReward.weeklyBonus?.xp || 0),
      currency: newCurrency,
      stats: newStats,
      unallocatedStatPoints: profile.unallocatedStatPoints + totalStatPoints,
      streak: dailyReward.streakDay,
      longestStreak: Math.max(dailyReward.streakDay, profile.longestStreak),
      lastLoginDate: dailyRewardService.getTodayDateString(),
    };

    setProfile(updatedProfile);
    setShowDailyRewardModal(false);

    // Add level up results to queue if any
    if (levelUpResults.length > 0) {
      setLevelUpQueue(prev => [...prev, ...levelUpResults]);
    }

    // Check for streak milestones
    if (dailyReward.streakDay > profile.streak && [3, 7, 30, 100].includes(dailyReward.streakDay)) {
      soundService.streakMilestone();
    }

    // Show notification with stat points if any
    const statPointsMsg = totalStatPoints > 0 ? `, +${totalStatPoints} Stat Points` : '';
    const levelUpMsg = levelUpResults.length > 0 ? ` 🎊 LEVEL UP x${levelUpResults.length}!` : '';
    showNotification(`🎁 Daily reward claimed! +${dailyReward.xp} XP, +${dailyReward.currency} Gold${statPointsMsg}${levelUpMsg}`, 'success');
    
    // Play daily reward sound
    soundService.dailyReward();

    // Play level up sound if leveled up
    if (levelUpResults.length > 0) {
      soundService.levelUp();
    }

    // Sync to Firestore (ensure no stale writes overwrite this)
    if (user) {
      // Clear any pending older profile writes and immediately persist the latest
      firestoreSyncService.clearQueue();
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
      firestoreSyncService.forceSync();
    }
  };

  const refreshProfile = () => {
    if (!profile) return;
    
    // Sync current profile state to Firestore (debounced)
    if (user) {
      firestoreSyncService.syncProfile(user.uid, profile);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!profile) return;
    
    // 🔒 SERVER-SIDE SANITIZATION for user-provided text fields
    let sanitizedUpdates = { ...updates };
    
    if (updates.name) {
      const sanitizeResult = await inputSanitizationService.sanitizeProfileInput(updates.name);
      
      if (sanitizeResult.warnings.length > 0) {
        console.warn('⚠️ Profile input was sanitized:', sanitizeResult.warnings);
        showNotification('Display name was cleaned for security', 'warning');
      }
      
      sanitizedUpdates.name = sanitizeResult.displayName;
    }
    
    const updatedProfile = { ...profile, ...sanitizedUpdates };
    setProfile(updatedProfile);
    
    // Optimistic update: sync in background (debounced)
    if (user) {
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
    }
  };

  const refreshQuests = () => {
    // Sync current quests state to Firestore (debounced)
    if (user) {
      firestoreSyncService.syncQuests(user.uid, quests);
    }
  };

  const refreshAchievements = async () => {
    if (!user) return;
    // Load achievements from Firestore
    const firestoreAchievements = await firestoreService.getAchievements(user.uid);
    if (firestoreAchievements) {
      setAchievements(firestoreAchievements);
    }
  };

  // 🚀 NEW: Check and generate next phase quests if needed
  const checkAndGenerateNextPhase = async (program: Program, customQuests?: Quest[]) => {
    const questsToCheck = customQuests || quests;
    const shouldGenerate = programService.shouldGenerateNextPhase(program, questsToCheck);

    if (!shouldGenerate) {
      console.log(`❌ shouldGenerateNextPhase returned false, not generating next phase`);
      return;
    }

    // Use the same phase determination logic as shouldGenerateNextPhase
    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    let currentPhase: number;
    if (isAIProgram) {
      currentPhase = programService.getCurrentPhaseForAIProgram(program, questsToCheck);
      console.log(`🚀 AI Program - Current phase determined: ${currentPhase} (based on quest completion)`);
    } else {
      currentPhase = programService.getCurrentPhase(program);
      console.log(`🚀 Traditional Program - Current phase determined: ${currentPhase} (based on date)`);
    }

    // Determine which phase to generate
    // If current phase has no quests, generate current phase
    // If current phase has quests (and is nearly complete), generate next phase
    const currentPhaseQuests = questsToCheck.filter(quest => {
      if (!quest.programId || quest.programId !== program.id) return false;
      if (!quest.scheduledFor) return false;

      const questDate = new Date(quest.scheduledFor);
      const startDate = new Date(program.startDate);
      const dayOffset = Math.floor((questDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const questPhase = Math.floor(dayOffset / 7) + 1;

      return questPhase === currentPhase;
    });

    const phaseToGenerate = currentPhaseQuests.length === 0 ? currentPhase : currentPhase + 1;

    console.log(`🚀 Current phase: ${currentPhase}, quests in current phase: ${currentPhaseQuests.length}`);
    console.log(`🚀 Phase to generate: ${phaseToGenerate}`);
    console.log(`🚀 Program total phases: ${program.totalPhases}`);

    // 🛡️ SAFEGUARD: Check if the phase we want to generate already has quests
    const phaseToGenerateQuests = questsToCheck.filter(quest => {
      if (!quest.programId || quest.programId !== program.id) return false;
      if (!quest.scheduledFor) return false;

      const questDate = new Date(quest.scheduledFor);
      const startDate = new Date(program.startDate);
      const dayOffset = Math.floor((questDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const questPhase = Math.floor(dayOffset / 7) + 1;

      return questPhase === phaseToGenerate;
    });

    if (phaseToGenerateQuests.length > 0) {
      console.log(`⚠️ Phase ${phaseToGenerate} already has ${phaseToGenerateQuests.length} quests - skipping generation to avoid duplicates`);
      return;
    }

    // Don't generate beyond total phases
    if (phaseToGenerate > program.totalPhases) {
      console.log(`⚠️ Phase ${phaseToGenerate} exceeds total phases (${program.totalPhases}) - program complete`);
      return;
    }

    // 🔒 LOCK: Check if this phase is already being generated (client-side)
    const lockKey = `${program.id}-${phaseToGenerate}`;
    if (generatingPhaseRef.current.has(lockKey)) {
      console.log(`🔒 Phase ${phaseToGenerate} for program ${program.id} is already being generated (client-side lock) - skipping`);
      return;
    }

    // 🛡️ FAIL-SAFE: Try to acquire distributed lock (Firestore-based)
    if (!user) {
      console.log('User not available for lock acquisition');
      return;
    }
    const lockAcquired = await firestoreService.acquireGenerationLock(program.id, phaseToGenerate, user.uid);
    if (!lockAcquired) {
      console.log(`🔒 Failed to acquire distributed lock for Phase ${phaseToGenerate} - another process is generating it`);
      return;
    }

    // Acquire client-side lock
    generatingPhaseRef.current.add(lockKey);
    console.log(`🔒 Lock acquired for ${lockKey} (client + distributed)`);

    // 🛡️ FAIL-SAFE: Update generation status to in_progress
    await firestoreService.updateProgram(program.id, {
      generationStatus: {
        phase: phaseToGenerate,
        status: 'in_progress',
        startedAt: Date.now()
      }
    } as any);

    try {
      console.log(`🚀 Generating progressive quests for week ${phaseToGenerate} using AI...`);
      
      // Call the Cloud Function for AI-powered progressive quest generation
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const generateProgressiveQuestsFunc = httpsCallable(functions, 'generateProgressiveQuests');
      
      const result = await generateProgressiveQuestsFunc({
        program,
        phase: phaseToGenerate,
        userId: user?.uid || ''
      });

      const response = result.data as any;
      
      if (!response.success) {
        console.error('Progressive quest generation failed:', response.message);
        return;
      }

      const { quests: nextPhaseQuests } = response.data;
      
      console.log(`🚀 AI Generated ${nextPhaseQuests.length} quests for week ${phaseToGenerate}:`, nextPhaseQuests.map((q: any) => ({
        title: q.title,
        dayOfPhase: q.dayOfPhase,
        subTasks: q.subTasks?.length || 0,
        subTasksContent: q.subTasks || []
      })));

      console.log(`🚀 Program details - startDate: ${program.startDate}, availableDays: ${program.availableDays}, phaseToGenerate: ${phaseToGenerate}`);
      
      if (nextPhaseQuests.length > 0) {
        // Create quests for the next phase with proper scheduling
        const questsToCreate = nextPhaseQuests.map((q: any) => {
          // Calculate scheduled date based on availableDays for the next phase
          let scheduledDate: Date;

          if (program.availableDays && program.availableDays.length > 0) {
            // AI should generate dayOfPhase as sequential numbers (1, 2, 3, ...)
            // Map them to the corresponding available day from the array
            const dayIndex = q.dayOfPhase - 1; // Convert to 0-based index
            const targetDayOfWeek = program.availableDays[dayIndex];

            console.log(`🚀 Progressive Scheduling debug - dayOfPhase: ${q.dayOfPhase}, dayIndex: ${dayIndex}, targetDayOfWeek: ${targetDayOfWeek}`);
            console.log(`🚀 Available days array: ${JSON.stringify(program.availableDays)} - Mapping: 1→${program.availableDays[0]}, 2→${program.availableDays[1]}, 3→${program.availableDays[2]}`);

            if (targetDayOfWeek !== undefined) {
              // Find the target day in the specific week
              const programStartDate = new Date(program.startDate);
              const weekStartDate = new Date(programStartDate);
              weekStartDate.setDate(weekStartDate.getDate() + (phaseToGenerate - 1) * 7); // Start of target week

              const weekStartDayOfWeek = weekStartDate.getDay();

              console.log(`🚀 Week calculation - programStartDate: ${programStartDate.toDateString()}, weekStartDate: ${weekStartDate.toDateString()}, weekStartDayOfWeek: ${weekStartDayOfWeek}`);

              // Calculate days to add to reach the target day within this week
              let daysToAdd = targetDayOfWeek - weekStartDayOfWeek;
              if (daysToAdd < 0) {
                daysToAdd += 7; // Wrap to next week if needed
              }

              scheduledDate = new Date(weekStartDate);
              scheduledDate.setDate(scheduledDate.getDate() + daysToAdd);

              console.log(`🚀 Final scheduling - daysToAdd: ${daysToAdd}, scheduledDate: ${scheduledDate.toDateString()}`);
              console.log(`🚀 Scheduling Week ${phaseToGenerate} Quest: ${q.title} for available day ${targetDayOfWeek} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][targetDayOfWeek]})`);
            } else {
              // Fallback to sequential if something goes wrong
              const startDate = new Date(program.startDate);
              const questDay = (phaseToGenerate - 1) * 7 + (q.dayOfPhase - 1);
              scheduledDate = new Date(startDate);
              scheduledDate.setDate(scheduledDate.getDate() + questDay);
              console.log(`🚀 Scheduling Week ${phaseToGenerate} Quest (fallback): ${q.title} for day ${q.dayOfPhase}`);
            }
          } else {
            // Fallback: no availableDays specified, use sequential
            const startDate = new Date(program.startDate);
            const questDay = (phaseToGenerate - 1) * 7 + (q.dayOfPhase - 1);
            scheduledDate = new Date(startDate);
            scheduledDate.setDate(scheduledDate.getDate() + questDay);
            console.log(`🚀 Scheduling Week ${phaseToGenerate} Quest (no available days): ${q.title} for day ${q.dayOfPhase}`);
          }

          const scheduledFor = scheduledDate.toISOString().split('T')[0];

          return {
            title: `[${program.title.split(' ')[0]}] ${q.title}`,
            description: q.description,
            type: q.type,
            difficulty: q.difficulty,
            resetDaily: false,
            scheduledFor: scheduledFor,
            scheduledTime: program.preferredTimeStart && program.preferredTimeEnd ? {
              start: program.preferredTimeStart,
              end: program.preferredTimeEnd
            } : undefined,
            allDay: false,
            programId: program.id,
            resources: q.resources || [],
            subTasks: q.subTasks?.map((taskText: string, index: number) => ({
              id: `subtask_${phaseToGenerate}_${q.dayOfPhase}_${index}`,
              text: taskText,
              completed: false,
              completedAt: null
            })) || [],
          };
        });

        // Add quests for next phase
        await createQuestsBatch(questsToCreate, true);
        
        // Update program with new quest count and generation status
        const phasesGenerated = program.phasesGenerated || [];
        const updatedProgram = {
          ...program,
          questsGenerated: program.questsGenerated + nextPhaseQuests.length,
          // 🛡️ FAIL-SAFE: Mark phase as completed
          generationStatus: {
            phase: phaseToGenerate,
            status: 'completed' as const,
            startedAt: program.generationStatus?.startedAt || Date.now(),
            completedAt: Date.now()
          },
          phasesGenerated: [...phasesGenerated, phaseToGenerate]
        };

        setPrograms(prev => prev.map(p => p.id === program.id ? updatedProgram : p));
        if (user) {
          await firestoreService.saveProgram(user.uid, updatedProgram);
        }
        
        // 🛡️ FAIL-SAFE: Release distributed lock
        await firestoreService.releaseGenerationLock(program.id, phaseToGenerate);
        
        showNotification(`✨ Generated Week ${phaseToGenerate} quests! Keep up the great progress!`, 'success');
      }
    } catch (error: any) {
      console.error('Error generating next phase quests:', error);
      
      // 🛡️ FAIL-SAFE: Mark generation as failed
      try {
        await firestoreService.updateProgram(program.id, {
          generationStatus: {
            phase: phaseToGenerate,
            status: 'failed',
            error: error.message || 'Generation failed',
            completedAt: Date.now(),
            retryCount: (program.generationStatus?.retryCount || 0) + 1
          }
        } as any);
      } catch (updateError) {
        console.error('Failed to update error status:', updateError);
      }
      
      // Don't show error to user as this is background generation
    } finally {
      // 🔒 Release locks
      generatingPhaseRef.current.delete(lockKey);
      await firestoreService.releaseGenerationLock(program.id, phaseToGenerate);
      console.log(`🔒 Lock released for ${lockKey}`);
    }
  };

  // 🎯 IMPROVED UX: Prioritize achievements to prevent modal spam
  const prioritizeAchievements = (achievements: Achievement[]): { modal: Achievement[]; notifications: Achievement[] } => {
    // Priority order: Quest achievements > Level achievements > Others
    const questAchievements = achievements.filter(a => a.category === 'quest');
    const levelAchievements = achievements.filter(a => a.category === 'level' && a.id !== 'rank_civilian'); // Exclude Civilian
    const otherAchievements = achievements.filter(a => a.category !== 'quest' && a.category !== 'level');
    
    // Show only the most important quest achievement as modal (usually "First Steps")
    const modalAchievements = questAchievements.length > 0 ? [questAchievements[0]] : [];
    
    // Show level achievements and other achievements as notifications only
    // Civilian achievement is pre-unlocked, so it won't appear here
    const notificationAchievements = [...levelAchievements, ...otherAchievements, ...questAchievements.slice(1)];
    
    return {
      modal: modalAchievements,
      notifications: notificationAchievements
    };
  };

  const completeQuest = async (questId: string) => {
    if (!profile) return;

    // In demo mode, we don't need a user
    if (!isDemoMode && !user) return;

    // Find and complete the quest
    const questIndex = quests.findIndex(q => q.id === questId);
    if (questIndex === -1) {
      console.error(`❌ Quest with ID ${questId} not found in quests array`);
      return;
    }

    // ⚡ OPTIMISTIC UPDATE: Store original state for rollback
    const originalQuest = quests[questIndex];
    const originalProfile = profile;

    const quest = {
      ...quests[questIndex],
      status: 'completed' as const,
      completedAt: Date.now(),
    };

    // Update quests array
    const updatedQuests = [...quests];
    updatedQuests[questIndex] = quest;

    // Check if completing a penalty quest
    const isPenaltyQuest = profile.penaltyActive && profile.penaltyQuestIds.includes(questId);
    
    // Check if penalty is active and this isn't a penalty quest (no XP)
    const xpReward = (profile.penaltyActive && !isPenaltyQuest) ? 0 : quest.xpReward;
    
    // Prepare reward data for animation
    const rewards = {
      xp: xpReward,
      currency: quest.currencyReward,
      stats: quest.statRewards
    };

    // Show quest complete animation
    setQuestCompleteData({ quest, rewards });

    // If penalty XP blocked, show warning notification
    if (profile.penaltyActive && !isPenaltyQuest && quest.xpReward > 0) {
      showNotification('⚠️ PENALTY ACTIVE: No XP awarded! Complete penalty quests first.', 'warning');
    }

    // Update lastActivityDate (but don't touch streak - that's managed by daily rewards)
    const today = new Date().toISOString().split('T')[0];

    // Award XP and calculate level ups (use modified xpReward)
    let newXP = profile.currentXP + xpReward;
    let newLevel = profile.level;
    let xpToNext = profile.xpToNextLevel;
    const levelUpResults: LevelUpResult[] = [];

    // Calculate total stat points earned from level ups
    let statPointsEarned = 0;
    
    while (newXP >= xpToNext) {
      newXP -= xpToNext;
      newLevel++;
      
      // Give 3 stat points per level up for manual allocation
      statPointsEarned += 3;
      
      levelUpResults.push({
        newLevel,
        statIncreases: {}, // No auto stat allocation - player chooses
        newTitle: getTitleForLevel(newLevel)
      });
      
      xpToNext = Math.floor(100 * Math.pow(1.1, newLevel - 1));
    }

    // Convert quest stat rewards to stat points instead of auto-applying
    const questStatPoints = Object.values(quest.statRewards).reduce((sum: number, val: number) => sum + val, 0);
    statPointsEarned += questStatPoints;

    // No auto stat allocation - all stats are now manual
    const newStats = { ...profile.stats };

    // Check if penalty should be cleared
    let penaltyActive = profile.penaltyActive;
    let penaltyQuestIds = [...profile.penaltyQuestIds];
    
    if (isPenaltyQuest) {
      // Remove this quest from penalty list
      penaltyQuestIds = penaltyQuestIds.filter(id => id !== questId);
      
      // Clear penalty if all penalty quests are completed
      if (penaltyQuestIds.length === 0) {
        penaltyActive = false;
        showNotification('✅ PENALTY CLEARED: XP gains restored!', 'success');
        soundService.penaltyCleared();
      }
    }
    
    // Update lifetime stats
    const updatedLifetimeStats = {
      ...profile.lifetimeStats,
      totalQuestsCompleted: profile.lifetimeStats.totalQuestsCompleted + 1,
      totalXPEarned: profile.lifetimeStats.totalXPEarned + quest.xpReward,
      totalCurrencyEarned: profile.lifetimeStats.totalCurrencyEarned + quest.currencyReward,
      questsByDifficulty: {
        ...profile.lifetimeStats.questsByDifficulty,
        [quest.difficulty]: profile.lifetimeStats.questsByDifficulty[quest.difficulty] + 1,
      },
      questsByType: {
        ...profile.lifetimeStats.questsByType,
        [quest.type]: profile.lifetimeStats.questsByType[quest.type] + 1,
      },
    };

    // Update profile
    const updatedProfile: UserProfile = {
      ...profile,
      currentXP: newXP,
      level: newLevel,
      xpToNextLevel: xpToNext,
      totalXP: profile.totalXP + xpReward,
      currentTitle: levelUpResults.length > 0 ? levelUpResults[levelUpResults.length - 1].newTitle! : profile.currentTitle,
      stats: newStats,
      unallocatedStatPoints: profile.unallocatedStatPoints + statPointsEarned,
      currency: profile.currency + quest.currencyReward,
      // Don't update streak here - it's managed by daily rewards only
      lastActivityDate: today,
      penaltyActive,
      penaltyQuestIds,
      lifetimeStats: updatedLifetimeStats,
    };

    // Track statistics
    statisticsService.updateTodayStats(1, quest.xpReward, 0, quest.currencyReward);

    // Check if this quest is from a program and update program progress
    let updatedProgramForGeneration: Program | undefined;
    if (quest.programId && !isDemoMode) {
      const program = programs.find(p => p.id === quest.programId);

      if (program && user) {
        const updatedProgram = {
          ...program,
          questsCompleted: program.questsCompleted + 1,
        };
        updatedProgramForGeneration = updatedProgram;
        setPrograms(prev => prev.map(p => p.id === quest.programId ? updatedProgram : p));
        await firestoreService.updateProgram(updatedProgram.id, { questsCompleted: program.questsCompleted + 1 });
      }
    }

    // Optimistic update: Update UI immediately
    setProfile(updatedProfile);
    setQuests(updatedQuests);

    // Play quest complete sound
    soundService.questComplete();

    // Skip Firestore operations in demo mode
    if (isDemoMode) {
      // Show conversion modal after 2-3 quest completions
      const completedQuests = quests.filter(q => q.status === 'completed').length;
      if (completedQuests >= 2 && !showDemoConversion) {
        setShowDemoConversion(true);
      }
      // Don't return early - continue to achievement checking
    }

    // 🚀 CHECK MILESTONES: Update milestone completion status immediately when quest is completed
    if (quest.programId) {
      const program = updatedProgramForGeneration || programs.find(p => p.id === quest.programId);
      if (program) {
        // Check and complete milestones based on quest completion
        const completedMilestones = programService.checkMilestones(program, () => updatedQuests);

        // If milestones were completed, update the program
        if (completedMilestones.length > 0) {
          const updatedProgramWithMilestones = {
            ...program,
            milestones: program.milestones.map(milestone => {
              const completedMilestone = completedMilestones.find(cm => cm.id === milestone.id);
              return completedMilestone || milestone;
            })
          };

          // Update the program in the updatedProgramForGeneration if it exists
          if (updatedProgramForGeneration) {
            updatedProgramForGeneration = updatedProgramWithMilestones;
          }

          // Update programs array
          setPrograms(prevPrograms =>
            prevPrograms.map(p => p.id === program.id ? updatedProgramWithMilestones : p)
          );

          // Award rewards and show milestone completion notifications
          let milestoneProfile = updatedProfile;
          completedMilestones.forEach(milestone => {
            showNotification(`🏆 Milestone completed: ${milestone.title}!`, 'achievement');
            
            // Award milestone rewards (XP, currency, stat points)
            if (milestone.reward) {
              const reward = milestone.reward;
              const xpGained = reward.xp || 0;
              const currencyGained = reward.currency || 0;
              const statPointsGained = reward.statPoints || 0;

              if (xpGained > 0 || currencyGained > 0 || statPointsGained > 0) {
                let updates: any = {
                  totalXP: milestoneProfile.totalXP + xpGained,
                  currency: milestoneProfile.currency + currencyGained,
                  unallocatedStatPoints: milestoneProfile.unallocatedStatPoints + statPointsGained,
                };

                // Handle XP and potential level ups
                if (xpGained > 0) {
                  let currentXP = milestoneProfile.currentXP + xpGained;
                  let currentLevel = milestoneProfile.level;
                  let xpToNextLevel = milestoneProfile.xpToNextLevel;
                  let additionalStatPoints = 0;

                  while (currentXP >= xpToNextLevel) {
                    currentXP -= xpToNextLevel;
                    currentLevel++;
                    const statPointsFromLevelUp = 3;
                    additionalStatPoints += statPointsFromLevelUp;

                    xpToNextLevel = 100 + (currentLevel - 1) * 50;
                  }

                  updates.currentXP = currentXP;
                  updates.level = currentLevel;
                  updates.xpToNextLevel = xpToNextLevel;
                  updates.unallocatedStatPoints = milestoneProfile.unallocatedStatPoints + statPointsGained + additionalStatPoints;

                  // Update current title if leveled up
                  if (currentLevel > milestoneProfile.level) {
                    updates.currentTitle = getTitleForLevel(currentLevel);
                  }
                }

                // Update the profile with rewards
                milestoneProfile = { ...milestoneProfile, ...updates };

                // Show reward notification
                const rewardParts = [];
                if (xpGained > 0) rewardParts.push(`+${xpGained} XP`);
                if (currencyGained > 0) rewardParts.push(`+${currencyGained} 💰`);
                if (statPointsGained > 0) rewardParts.push(`+${statPointsGained} Stat Points`);
                
                if (rewardParts.length > 0) {
                  showNotification(`🎁 Milestone Rewards: ${rewardParts.join(', ')}`, 'success');
                }
              }
            }
          });
          
          // Update profile if milestones were completed with rewards
          if (milestoneProfile !== updatedProfile) {
            setProfile(milestoneProfile);
          }
        }
      }
    }

    // 🚀 PROGRESSIVE GENERATION: Check if we need to generate next phase quests
    // Use the updatedQuests array and updatedProgramForGeneration instead of stale state
    if (quest.programId) {
      const program = updatedProgramForGeneration || programs.find(p => p.id === quest.programId);
      if (program) {
        // Check and generate next phase in background (don't await to avoid blocking UI)
        checkAndGenerateNextPhase(program, updatedQuests).catch(error => {
          console.error('Error generating next phase:', error);
        });
      } else {
        console.log(`❌ Program not found for quest.programId: ${quest.programId}`);
      }
    } else {
      console.log(`❌ Quest completed does not have programId, skipping progressive generation check`);
    }
    
    // Check if title changed and play rank announcement
    if (levelUpResults.length > 0 && profile.currentTitle !== updatedProfile.currentTitle) {
      const newTitle = updatedProfile.currentTitle;
      // Extract rank letter from title (e.g., "S-Rank Hunter" -> "S")
      if (newTitle === 'Shadow Monarch') {
        soundService.shadowMonarch();
      } else {
        const rankMatch = newTitle.match(/^([EDCBAS])-Rank/);
        if (rankMatch) {
          soundService.titleAcquired(rankMatch[1]);
        }
      }
    }

    // ⚡ OPTIMISTIC UPDATE: Critical operation with error handling
    const todayStats = statisticsService.getTodayStats();
    
    // Skip Firestore sync in demo mode
    if (!isDemoMode) {
      try {
        if (user) {
          await firestoreSyncService.batchSyncAll(
            user.uid,
            updatedProfile,
            updatedQuests,
            todayStats || undefined
          );
        }
      } catch (error) {
        console.error('❌ Error syncing quest completion:', error);
        // ❌ REVERT on error
        setProfile(originalProfile);
        setQuests(prev => prev.map(q => q.id === questId ? originalQuest : q));
        showNotification('❌ Failed to complete quest. Please try again.', 'warning');
        return; // Exit early to prevent achievement checks with reverted state
      }
    }

    // Check for achievements
    const completedQuests = updatedQuests.filter(q => q.status === 'completed');
    const totalStats = Object.values(updatedProfile.stats).reduce((a, b) => a + b, 0);
    
    // Fix Perfect Raid Day achievement: Only count daily quests created today
    const todayString = new Date().toDateString();
    const todaysDailyQuests = updatedQuests.filter(q => 
      q.type === 'daily' && new Date(q.createdAt).toDateString() === todayString
    );
    const todaysDailyCompleted = todaysDailyQuests.filter(q => q.status === 'completed');
    const bossCompleted = completedQuests.filter(q => q.type === 'boss').length;

    const { updatedAchievements, newlyUnlocked } = achievementService.checkAchievements(
      achievements, // Pass current achievements
      completedQuests.length,
      updatedProfile.level,
      updatedProfile.streak,
      totalStats,
      todaysDailyCompleted.length,
      todaysDailyQuests.length,
      bossCompleted
    );


    // ⏰ DELAYED MODAL TRIGGERS: Wait for quest completion animation to finish
    // This prevents modal overlap and gives users time to see quest completion
    const modalDelay = 1200; // 1.2 seconds (slightly after quest animation completes)
    
    if (newlyUnlocked.length > 0) {
      // Update achievements in state immediately
      setAchievements(updatedAchievements);
      
      // Save to Firestore or demo data
      if (user) {
        await firestoreService.saveAchievements(user.uid, updatedAchievements);
      } else if (isDemoMode) {
        // Save achievements to demo user data
        const demoUserStr = sessionStorage.getItem('demo_user');
        if (demoUserStr) {
          const demoUser = JSON.parse(demoUserStr);
          demoUser.achievements = updatedAchievements;
          sessionStorage.setItem('demo_user', JSON.stringify(demoUser));
        }
      }
      
      // 🎯 IMPROVED UX: Prioritize achievements to prevent modal spam
      const prioritizedAchievements = prioritizeAchievements(newlyUnlocked);
      
      // Show only the most important achievement as a modal
      if (prioritizedAchievements.modal.length > 0) {
        setTimeout(() => {
          setAchievementQueue(prev => [...prev, ...prioritizedAchievements.modal]);
          soundService.achievementUnlocked();

          // Show notification for the modal achievement
          prioritizedAchievements.modal.forEach(achievement => {
            showNotification(`Achievement Unlocked: ${achievement.title}`, 'achievement');
          });
        }, modalDelay + 1200); // Increased to 1200ms after level up modal for better spacing
      }
      
      // Show other achievements as notifications only (no modal spam)
      if (prioritizedAchievements.notifications.length > 0) {
        setTimeout(() => {
          prioritizedAchievements.notifications.forEach(achievement => {
            showNotification(`Achievement Unlocked: ${achievement.title}`, 'achievement');
          });
        }, modalDelay + 1200); // Show after modal achievement
      }
    }

    // Streak milestones are now checked in daily reward claiming

    // Add level up results to queue with delay
    if (levelUpResults.length > 0) {
      // Delay level up modal display
      setTimeout(() => {
        setLevelUpQueue(prev => [...prev, ...levelUpResults]);
        soundService.levelUp();
        
        // Show notification for level up
        levelUpResults.forEach(result => {
          showNotification(`Level Up! You are now Level ${result.newLevel}`, 'levelup');
        });
      }, modalDelay);
    }
  };

  const createQuest = async (
    title: string,
    description: string,
    type: any,
    difficulty: any,
    resetDaily: boolean = false,
    scheduledFor?: string,
    subTasks?: SubTask[],
    scheduledTime?: { start: string; end: string },
    allDay?: boolean
  ) => {
    if (!user) return;
    
    // ✅ RATE LIMITING CHECK
    if (!checkRateLimit(user.uid, rateLimitPresets.questCreate)) {
      const resetIn = Math.ceil(getRateLimitReset(user.uid, 'quest_create') / 1000);
      showNotification(`⚠️ Too many requests. Please wait ${resetIn} seconds.`, 'warning');
      return;
    }

    // ✅ VALIDATE INPUT DATA (structure, types, formats only)
    // Security sanitization happens server-side
    const validationResult = validateInput(questCreateSchema, {
      title,
      description,
      type,
      difficulty,
      resetDaily,
      scheduledFor,
      subTasks,
      scheduledTime,
      allDay,
    });

    if (!validationResult.success) {
      showNotification(`❌ Validation failed: ${validationResult.error}`, 'warning');
      console.error('Quest validation failed:', validationResult.error);
      return;
    }

    // Use validated and sanitized data
    const validatedData = validationResult.data;
    
    // 🔒 SERVER-SIDE SANITIZATION (Additional security layer)
    const sanitizeResult = await inputSanitizationService.sanitizeQuestInput(
      validatedData.title,
      validatedData.description
    );
    
    // Log warnings if input was modified
    if (sanitizeResult.warnings.length > 0) {
      console.warn('⚠️ Quest input was sanitized:', sanitizeResult.warnings);
      showNotification('Input was cleaned for security', 'warning');
    }
    
    const difficultyRewards = {
      easy: { xp: 10, currency: 1, stats: { strength: 1 } },
      normal: { xp: 20, currency: 2, stats: { intelligence: 1, sense: 1 } },
      hard: { xp: 40, currency: 4, stats: { agility: 2, vitality: 2 } },
      boss: { xp: 100, currency: 10, stats: { strength: 3, intelligence: 3 } }
    };
    
    const rewards = difficultyRewards[validatedData.difficulty as keyof typeof difficultyRewards];
    
    // Calculate estimated duration if time is provided
    let estimatedDuration: number | undefined = validatedData.estimatedDuration;
    if (!estimatedDuration && validatedData.scheduledTime) {
      const [startHour, startMin] = validatedData.scheduledTime.start.split(':').map(Number);
      const [endHour, endMin] = validatedData.scheduledTime.end.split(':').map(Number);
      estimatedDuration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    }
    
    const newQuest: Quest = {
      id: `quest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: user.uid, // CRITICAL: Required for Firestore security rules
      title: sanitizeResult.title,
      description: sanitizeResult.description,
      type: validatedData.type,
      difficulty: validatedData.difficulty,
      xpReward: rewards.xp,
      currencyReward: rewards.currency,
      statRewards: rewards.stats,
      status: 'active',
      createdAt: Date.now(),
      completedAt: null,
      resetDaily: validatedData.resetDaily ?? false, // Default to false if undefined
      scheduledFor: validatedData.scheduledFor,
      scheduledTime: validatedData.scheduledTime,
      allDay: validatedData.allDay ?? false, // Default to false if undefined
      estimatedDuration,
      subTasks: validatedData.subTasks,
      customColor: validatedData.customColor,
    };

    // FIX: Use functional state update to prevent race conditions
    setQuests(prevQuests => [...prevQuests, newQuest]);
    
    // Show notification
    showNotification(`Quest Created: ${validatedData.title}`, 'quest');
    
    // CRITICAL: Force immediate sync (don't wait for debounce)
    // Save single quest immediately to Firestore
    firestoreService.saveQuest(user.uid, newQuest).catch(error => {
      console.error('Error saving quest:', error);
    });
  };

  // Batch create multiple quests at once (for AI generation)
  const createQuestsBatch = async (questsData: Array<{
    title: string;
    description: string;
    type: any;
    difficulty: any;
    resetDaily?: boolean;
    scheduledFor?: string;
    subTasks?: SubTask[];
    programId?: string;
    resources?: string[];
    scheduledTime?: { start: string; end: string };
    allDay?: boolean;
  }>, bypassRateLimit: boolean = false) => {
    const difficultyRewards = {
      easy: { xp: 10, currency: 1, stats: { strength: 1 } },
      normal: { xp: 20, currency: 2, stats: { intelligence: 1, sense: 1 } },
      hard: { xp: 40, currency: 4, stats: { agility: 2, vitality: 2 } },
      boss: { xp: 100, currency: 10, stats: { strength: 3, intelligence: 3 } }
    };

    // ⚡ PERFORMANCE OPTIMIZATION: Skip sanitization for AI-generated content
    // 
    // Rationale for skipping sanitization:
    // 1. OpenAI has strong safety guardrails - won't generate malicious code
    // 2. React escapes output by default - XSS protection built-in
    // 3. Firestore stores text, not executable code - no injection risk
    // 4. AI models trained to resist prompt injection
    // 5. Saves 60+ Cloud Function calls per program (significant cost reduction)
    // 6. Reduces latency by 2-3 seconds for program generation
    // 
    // Security: User input IS sanitized before being sent to OpenAI in the prompt.
    // Only the AI's output is trusted here (generated in secure Cloud Function).

    const newQuests: Quest[] = questsData.map((questData, index) => {
      const rewards = difficultyRewards[questData.difficulty as keyof typeof difficultyRewards];
      
      return {
        id: `quest_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user?.uid || '',
        title: questData.title,
        description: questData.description,
        type: questData.type,
        difficulty: questData.difficulty,
        xpReward: rewards.xp,
        currencyReward: rewards.currency,
        statRewards: rewards.stats,
        status: 'active' as const,
        createdAt: Date.now() + index, // Ensure unique timestamps
        completedAt: null,
        resetDaily: questData.resetDaily || false,
        scheduledFor: questData.scheduledFor,
        scheduledTime: questData.scheduledTime,
        allDay: questData.allDay,
        subTasks: questData.subTasks,
        programId: questData.programId,
        resources: questData.resources,
      };
    });

    // Add all quests at once
    setQuests(prevQuests => [...prevQuests, ...newQuests]);
    
    // Show notification
    showNotification(`✨ Created ${newQuests.length} AI-generated quest${newQuests.length > 1 ? 's' : ''}!`, 'success');
    
    // Save all quests to Firestore
    newQuests.forEach(quest => {
      if (user) {
        firestoreService.saveQuest(user.uid, quest, bypassRateLimit).catch(error => {
          console.error('Error saving quest:', error);
        });
      }
    });
  };

  const updateQuest = async (questId: string, updates: Partial<Quest>, silent: boolean = false) => {
    // ✅ DETECT TIME BLOCK ADJUSTMENTS (calendar dragging)
    const isTimeBlockAdjustment = (
      updates.scheduledFor !== undefined ||
      updates.scheduledTime !== undefined ||
      updates.allDay !== undefined
    ) && (
      updates.title === undefined &&
      updates.description === undefined &&
      updates.type === undefined &&
      updates.difficulty === undefined &&
      updates.resetDaily === undefined &&
      updates.subTasks === undefined
    );

    // ✅ RATE LIMITING CHECK
    // Bypass for calendar time-block adjustments (dragging/resizing). No rate limit needed here.
    if (!isTimeBlockAdjustment) {
      const rateLimitPreset = rateLimitPresets.questUpdate;
      if (!isDemoMode && (!user || !checkRateLimit(user.uid, rateLimitPreset))) {
        const resetIn = Math.ceil(getRateLimitReset(user?.uid || '', 'quest_update') / 1000);
        showNotification(`⚠️ Too many requests. Please wait ${resetIn} seconds.`, 'warning');
        return;
      }
    }

    // ✅ VALIDATE UPDATE DATA (structure, types, formats only)
    // Security sanitization happens server-side
    const validationResult = validateInput(questUpdateSchema, updates);
    
    if (!validationResult.success) {
      showNotification(`❌ Validation failed: ${validationResult.error}`, 'warning');
      console.error('Quest update validation failed:', validationResult.error);
      return;
    }

    const validatedUpdates = validationResult.data;
    
    // 🔒 SERVER-SIDE SANITIZATION (if title or description are being updated)
    let sanitizedUpdates = { ...validatedUpdates };
    if (validatedUpdates.title || validatedUpdates.description) {
      const currentQuest = quests.find(q => q.id === questId);
      if (currentQuest) {
        const sanitizeResult = await inputSanitizationService.sanitizeQuestInput(
          validatedUpdates.title || currentQuest.title,
          validatedUpdates.description || currentQuest.description
        );
        
        if (sanitizeResult.warnings.length > 0) {
          console.warn('⚠️ Quest update input was sanitized:', sanitizeResult.warnings);
        }
        
        if (validatedUpdates.title) sanitizedUpdates.title = sanitizeResult.title;
        if (validatedUpdates.description) sanitizedUpdates.description = sanitizeResult.description;
      }
    }
    
    // ⚡ OPTIMISTIC UPDATE: Store original state for rollback
    const originalQuests = [...quests];
    
    const updatedQuests = quests.map(quest => 
      quest.id === questId ? { ...quest, ...sanitizedUpdates } : quest
    );
    
    // Update UI immediately
    setQuests(updatedQuests);
    
    // Only show notification if not silent
    if (!silent) {
      showNotification('Quest updated!', 'success');
    }
    
    // Find updated quest and save to Firestore
    const updatedQuest = updatedQuests.find(q => q.id === questId);
    if (updatedQuest) {
      try {
        if (user) {
          // Bypass rate limit for calendar drag/resize operations
          await firestoreService.saveQuest(user.uid, updatedQuest, isTimeBlockAdjustment);
        }
      } catch (error) {
        console.error('❌ Error updating quest:', error);
        // ❌ REVERT on error
        setQuests(originalQuests);
        showNotification('❌ Failed to update quest. Please try again.', 'warning');
      }
    }
  };

  const toggleSubTask = async (questId: string, subTaskId: string) => {
    // ⚡ OPTIMISTIC UPDATE: Store original state for rollback
    const originalQuests = [...quests];
    
    const updatedQuests = quests.map(quest => {
      if (quest.id === questId && quest.subTasks) {
        return {
          ...quest,
          subTasks: quest.subTasks.map(st =>
            st.id === subTaskId ? { ...st, completed: !st.completed } : st
          )
        };
      }
      return quest;
    });
    
    // Update UI immediately
    setQuests(updatedQuests);
    
    try {
      if (user) {
        await firestoreSyncService.syncQuests(user.uid, updatedQuests);
      }
    } catch (error) {
      console.error('❌ Error toggling subtask:', error);
      // ❌ REVERT on error
      setQuests(originalQuests);
      showNotification('❌ Failed to toggle subtask. Please try again.', 'warning');
    }
  };

  const deleteQuest = async (questId: string, silent: boolean = false, deleteSeries: boolean = false) => {
    // Check if this is a penalty quest - if so, don't allow deletion
    if (profile?.penaltyActive && profile.penaltyQuestIds.includes(questId)) {
      showNotification('❌ Cannot delete penalty quests. Use reschedule instead.', 'warning');
      return;
    }

    // Find quest before deleting
    const quest = quests.find(q => q.id === questId);
    if (!quest) return;

    // For daily quests being deleted as entire series, store this info
    if (deleteSeries && quest.resetDaily && user) {
      const deletedSeriesKey = `deleted_daily_series_${user.uid}`;
      const deletedSeries = JSON.parse(localStorage.getItem(deletedSeriesKey) || '[]');

      // Store quest details to prevent recreation
      const seriesInfo = {
        id: quest.id,
        title: quest.title,
        deletedAt: Date.now()
      };
      deletedSeries.push(seriesInfo);
      localStorage.setItem(deletedSeriesKey, JSON.stringify(deletedSeries));
    }
    
    // ⚡ OPTIMISTIC UPDATE: Store original state for rollback
    const originalQuests = [...quests];
    
    // Remove from UI immediately
    const updatedQuests = quests.filter(q => q.id !== questId);
    setQuests(updatedQuests);
    
    // Only show notification if not silent
    if (!silent) {
      showNotification(`Quest Deleted: ${quest.title}`, 'warning');
    }
    
    // Delete from Firestore immediately (not debounced)
    if (!isDemoMode) {
      try {
        await firestoreSyncService.deleteQuest(questId);
      } catch (error) {
        console.error('❌ Error deleting quest from Firestore:', error);
        // ❌ REVERT on error
        setQuests(originalQuests);
        showNotification('❌ Failed to delete quest. Please try again.', 'warning');
      }
    }
  };

  const rescheduleQuest = async (questId: string, newDate: string) => {
    // Find quest before rescheduling
    const quest = quests.find(q => q.id === questId);
    if (!quest) return;
    
    // ⚡ OPTIMISTIC UPDATE: Store original state for rollback
    const originalQuests = [...quests];
    const originalProfile = profile;
    
    // Update quest with new scheduled date
    const updatedQuests = quests.map(q => 
      q.id === questId 
        ? { ...q, scheduledFor: newDate }
        : q
    );
    setQuests(updatedQuests);
    
    // Remove from penalty quests if it was a penalty quest
    if (profile?.penaltyActive && profile.penaltyQuestIds.includes(questId)) {
      const updatedPenaltyQuestIds = profile.penaltyQuestIds.filter(id => id !== questId);
      const updatedProfile = {
        ...profile,
        penaltyQuestIds: updatedPenaltyQuestIds,
        // Clear penalty if no more penalty quests
        penaltyActive: updatedPenaltyQuestIds.length > 0
      };
      setProfile(updatedProfile);
      
      // Save to Firestore
      try {
        if (user) {
          await firestoreSyncService.syncProfile(user.uid, updatedProfile);
        }
      } catch (error) {
        console.error('❌ Error updating profile in Firestore:', error);
        // ❌ REVERT on error
        setQuests(originalQuests);
        setProfile(originalProfile);
        showNotification('❌ Failed to reschedule quest. Please try again.', 'warning');
        return;
      }
    }
    
    // Update quest in Firestore
    try {
      if (user) {
        await firestoreSyncService.syncQuests(user.uid, updatedQuests);
      }
      showNotification(`✅ Quest rescheduled to ${newDate}: ${quest.title}`, 'success');
    } catch (error) {
      console.error('❌ Error rescheduling quest in Firestore:', error);
      // ❌ REVERT on error
      setQuests(originalQuests);
      if (originalProfile) setProfile(originalProfile);
      showNotification('❌ Failed to reschedule quest. Please try again.', 'warning');
    }
  };

  const clearLevelUpQueue = () => {
    setLevelUpQueue([]);
  };

  const clearAchievementQueue = () => {
    setAchievementQueue([]);
  };

  const clearQuestComplete = () => {
    setQuestCompleteData(null);
  };

  const showNotification = (message: string, type: NotificationType) => {
    const id = `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setNotifications(prev => [...prev, { id, message, type }]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const purchaseItem = (itemId: string): boolean => {
    if (!profile) return false;
    
    const item = shopService.getItemById(itemId);
    if (!item || profile.currency < item.price) return false;

    // Fix: Ensure inventory is always an array (backward compatibility)
    const currentInventory = Array.isArray(profile.inventory) ? profile.inventory : [];

    let updatedProfile = {
      ...profile,
      currency: profile.currency - item.price,
      inventory: [...currentInventory, item],
    };

    let xpGained = 0;
    const levelUpResults: LevelUpResult[] = [];

    // Apply item effects
    if (item.effect) {
      if (item.effect.xpMultiplier !== undefined) {
        if (itemId === 'level_up') {
          // Instant level up
          xpGained = updatedProfile.xpToNextLevel - updatedProfile.currentXP;
        } else {
          xpGained = item.effect.xpMultiplier;
        }
      }
      if (item.effect.statBoost) {
        const newStats = { ...updatedProfile.stats };
        Object.entries(item.effect.statBoost).forEach(([stat, value]) => {
          newStats[stat as keyof typeof newStats] += value;
        });
        updatedProfile.stats = newStats;
      }
    }

    // Calculate level ups if XP was gained
    if (xpGained > 0) {
      let newXP = updatedProfile.currentXP + xpGained;
      let newLevel = updatedProfile.level;
      let xpToNext = updatedProfile.xpToNextLevel;
      let statPointsEarned = 0;

      // Check for level ups (same logic as completeQuest)
      while (newXP >= xpToNext) {
        newXP -= xpToNext;
        newLevel++;
        
        // Give 3 stat points per level up
        statPointsEarned += 3;
        
        levelUpResults.push({
          newLevel,
          statIncreases: {},
          newTitle: getTitleForLevel(newLevel)
        });
        
        xpToNext = Math.floor(100 * Math.pow(1.1, newLevel - 1));
      }

      // Update profile with level up results
      updatedProfile = {
        ...updatedProfile,
        currentXP: newXP,
        level: newLevel,
        xpToNextLevel: xpToNext,
        totalXP: profile.totalXP + xpGained,
        currentTitle: levelUpResults.length > 0 ? levelUpResults[levelUpResults.length - 1].newTitle! : profile.currentTitle,
        unallocatedStatPoints: profile.unallocatedStatPoints + statPointsEarned,
      };
    }
    
    soundService.coinCollect();
    setProfile(updatedProfile);
    
    // Show notification
    showNotification(`Purchased: ${item.name}`, 'item');

    // Add level up results to queue
    if (levelUpResults.length > 0) {
      setLevelUpQueue(prev => [...prev, ...levelUpResults]);
      soundService.levelUp();
      
      // Check if title changed and play rank announcement
      if (profile.currentTitle !== updatedProfile.currentTitle) {
        const newTitle = updatedProfile.currentTitle;
        if (newTitle === 'Shadow Monarch') {
          soundService.shadowMonarch();
        } else {
          const rankMatch = newTitle.match(/^([EDCBAS])-Rank/);
          if (rankMatch) {
            soundService.titleAcquired(rankMatch[1]);
          }
        }
      }
      
      // Show notification for level up
      levelUpResults.forEach(result => {
        showNotification(`Level Up! You are now Level ${result.newLevel}`, 'levelup');
      });
    }
    
    // Sync to Firestore (debounced)
    if (user) {
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
    }
    
    return true;
  };

  const completePomodoroSession = (focusMinutes: number) => {
    if (!profile) return;
    
    const xpReward = Math.floor(focusMinutes * 2); // 2 XP per minute
    const currencyReward = Math.floor(focusMinutes / 5); // 1 currency per 5 minutes
    
    // Calculate level ups
    let newXP = profile.currentXP + xpReward;
    let newLevel = profile.level;
    let xpToNext = profile.xpToNextLevel;
    const levelUpResults: LevelUpResult[] = [];
    let statPointsEarned = 0;

    // Check for level ups (same logic as completeQuest)
    while (newXP >= xpToNext) {
      newXP -= xpToNext;
      newLevel++;
      
      // Give 3 stat points per level up
      statPointsEarned += 3;
      
      levelUpResults.push({
        newLevel,
        statIncreases: {},
        newTitle: getTitleForLevel(newLevel)
      });
      
      xpToNext = Math.floor(100 * Math.pow(1.1, newLevel - 1));
    }
    
    const updatedProfile = {
      ...profile,
      currentXP: newXP,
      level: newLevel,
      xpToNextLevel: xpToNext,
      totalXP: profile.totalXP + xpReward,
      currentTitle: levelUpResults.length > 0 ? levelUpResults[levelUpResults.length - 1].newTitle! : profile.currentTitle,
      unallocatedStatPoints: profile.unallocatedStatPoints + statPointsEarned,
      currency: profile.currency + currencyReward,
      pomodoroSessions: profile.pomodoroSessions + 1,
      totalFocusTime: profile.totalFocusTime + focusMinutes,
    };
    
    // Track statistics
    statisticsService.updateTodayStats(0, xpReward, focusMinutes, currencyReward);
    
    soundService.questComplete();
    setProfile(updatedProfile);
    
    // Show notification
    showNotification(`Pomodoro Complete! +${xpReward} XP, +💰${currencyReward}`, 'success');

    // Add level up results to queue
    if (levelUpResults.length > 0) {
      setLevelUpQueue(prev => [...prev, ...levelUpResults]);
      soundService.levelUp();
      
      // Check if title changed and play rank announcement
      if (profile.currentTitle !== updatedProfile.currentTitle) {
        const newTitle = updatedProfile.currentTitle;
        if (newTitle === 'Shadow Monarch') {
          soundService.shadowMonarch();
        } else {
          const rankMatch = newTitle.match(/^([EDCBAS])-Rank/);
          if (rankMatch) {
            soundService.titleAcquired(rankMatch[1]);
          }
        }
      }
      
      // Show notification for level up
      levelUpResults.forEach(result => {
        showNotification(`Level Up! You are now Level ${result.newLevel}`, 'levelup');
      });
    }
    
    // Sync to Firestore (debounced)
    if (user) {
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
      const todayStats = statisticsService.getTodayStats();
      if (todayStats) {
        firestoreSyncService.syncStatistic(user.uid, todayStats);
      }
    }
  };

  const acceptPenalty = () => {
    if (!profile) return;
    
    // Activate penalty
    const penaltyQuestIds = failedDailyQuests.map(q => q.id);
    const updatedProfile: UserProfile = {
      ...profile,
      penaltyActive: true,
      penaltyQuestIds,
    };
    
    setProfile(updatedProfile);
    setShowPenaltyModal(false);
    
    // Show warning notification
    showNotification('⚠️ PENALTY ACTIVE: Complete failed daily quests to restore XP gains!', 'warning');
    
    // Play penalty sound
    soundService.penaltyActive();
    
    // Sync to Firestore
    if (user) {
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
    }
  };

  const allocateStatPoints = (statAllocations: Partial<Stats>) => {
    if (!profile) return;
    
    // Calculate total points being allocated
    const totalPoints = Object.values(statAllocations).reduce((sum: number, val: number) => sum + val, 0);
    
    if (totalPoints > profile.unallocatedStatPoints) {
      showNotification('❌ Not enough stat points available!', 'warning');
      return;
    }
    
    // Apply stat allocations
    const newStats = { ...profile.stats };
    Object.entries(statAllocations).forEach(([stat, value]) => {
      newStats[stat as keyof Stats] += value;
    });
    
    // Update profile
    const updatedProfile: UserProfile = {
      ...profile,
      stats: newStats,
      unallocatedStatPoints: profile.unallocatedStatPoints - totalPoints,
    };
    
    setProfile(updatedProfile);
    
    // Show notification
    showNotification(`✅ Allocated ${totalPoints} stat points!`, 'success');
    
    // Play stat allocation sound
    soundService.statAllocated();

    // Check for stat achievements
    const totalStats = Object.values(newStats).reduce((a, b) => a + b, 0);
    const completedQuests = quests.filter(q => q.status === 'completed');
    
    // Fix Perfect Raid Day achievement: Only count daily quests created today
    const todayString = new Date().toDateString();
    const todaysDailyQuests = quests.filter(q => 
      q.type === 'daily' && new Date(q.createdAt).toDateString() === todayString
    );
    const todaysDailyCompleted = todaysDailyQuests.filter(q => q.status === 'completed');
    const bossCompleted = completedQuests.filter(q => q.type === 'boss').length;

    const { updatedAchievements, newlyUnlocked } = achievementService.checkAchievements(
      achievements,
      completedQuests.length,
      updatedProfile.level,
      updatedProfile.streak,
      totalStats,
      todaysDailyCompleted.length,
      todaysDailyQuests.length,
      bossCompleted
    );

    if (newlyUnlocked.length > 0) {
      setAchievements(updatedAchievements);
      setAchievementQueue(prev => [...prev, ...newlyUnlocked]);
      
      // Sync achievements to Firestore
      if (user) {
        firestoreService.saveAchievements(user.uid, updatedAchievements);
      }
      
      // Play achievement sound with slight delay to avoid clashing with stat allocation sound
      setTimeout(() => {
        soundService.achievementUnlocked();
      }, 800); // 800ms delay after stat allocation sound
      
      // Show notification for each new achievement
      newlyUnlocked.forEach(achievement => {
        showNotification(`Achievement Unlocked: ${achievement.title}`, 'achievement');
      });
    }
    
    // Sync to Firestore
    if (user) {
      firestoreSyncService.syncProfile(user.uid, updatedProfile);
    }
  };

  // ========== PROGRAM MANAGEMENT ==========

  const createProgram = async (
    goal: string,
    duration: number,
    scheduleType: ProgramScheduleType,
    customDays: number[] | undefined,
    questsPerDay: number,
    intensity: ProgramIntensity,
    domain: ProgramDomain,
    domainAnswers: DomainAnswers,
    aiFollowUpAnswers?: { [key: string]: string },
    preferredTimeStart?: string,
    preferredTimeEnd?: string,
    availableDays?: number[],
    skillLevel?: 'beginner' | 'intermediate' | 'advanced',
    timePerDay?: number,
    learningStyle?: string,
    specificFocus?: string,
    availableResources?: string
  ): Promise<string> => { // Return program ID
    if (!profile || !user) throw new Error('User not authenticated');

    // 🔒 CHECK SUBSCRIPTION LIMIT FIRST
    const limitCheck = subscriptionService.canUseAIProgramGeneration(profile);
    if (!limitCheck.allowed) {
      showNotification(limitCheck.reason || 'You\'ve reached your monthly limit', 'warning');
      throw new Error(limitCheck.reason || 'Subscription limit reached');
    }

    // ✅ RATE LIMITING CHECK
    if (!checkRateLimit(user.uid, rateLimitPresets.programCreate)) {
      const resetIn = Math.ceil(getRateLimitReset(user.uid, 'program_create') / 60000);
      showNotification(`⚠️ Too many program creation requests. Please wait ${resetIn} minute(s).`, 'warning');
      throw new Error(`Rate limited. Try again in ${resetIn} minutes.`);
    }

    // ✅ VALIDATE INPUT DATA (structure, types, formats only)
    // Security sanitization happens server-side
    const validationResult = validateInput(programCreateSchema, {
      goal,
      duration,
      scheduleType,
      customDays,
      questsPerDay,
      intensity,
      domain,
      domainAnswers,
      aiFollowUpAnswers,
      skillLevel,
      timePerDay,
      learningStyle,
      specificFocus,
      availableResources,
    });

    if (!validationResult.success) {
      showNotification(`❌ Validation failed: ${validationResult.error}`, 'warning');
      console.error('Program creation validation failed:', validationResult.error);
      throw new Error(`Validation failed: ${validationResult.error}`);
    }

    const validatedData = validationResult.data;

    // 🔒 SERVER-SIDE SANITIZATION (Additional security layer)
    const goalSanitizeResult = await inputSanitizationService.sanitizeProgramInput(validatedData.goal);
    
    if (goalSanitizeResult.warnings.length > 0) {
      console.warn('⚠️ Program input was sanitized:', goalSanitizeResult.warnings);
    }
    
    // Sanitize optional user context strings
    let sanitizedLearningStyle = validatedData.learningStyle;
    let sanitizedSpecificFocus = validatedData.specificFocus;
    let sanitizedAvailableResources = validatedData.availableResources;
    
    if (validatedData.learningStyle) {
      const result = await inputSanitizationService.sanitizeInput(validatedData.learningStyle, { maxLength: 500 });
      sanitizedLearningStyle = result.sanitized;
    }
    if (validatedData.specificFocus) {
      const result = await inputSanitizationService.sanitizeInput(validatedData.specificFocus, { maxLength: 500 });
      sanitizedSpecificFocus = result.sanitized;
    }
    if (validatedData.availableResources) {
      const result = await inputSanitizationService.sanitizeInput(validatedData.availableResources, { maxLength: 1000 });
      sanitizedAvailableResources = result.sanitized;
    }

    try {
      setCurrentProgramDuration(duration);
      setShowProgramLoading(true);

      const schedule = {
        type: validatedData.scheduleType,
        customDays: validatedData.customDays,
        questsPerDay: validatedData.questsPerDay,
      };

      // User context for personalization (backward compatibility)
      const userContext = {
        skillLevel: validatedData.skillLevel,
        timePerDay: validatedData.timePerDay,
        learningStyle: sanitizedLearningStyle,
        specificFocus: sanitizedSpecificFocus,
        availableResources: sanitizedAvailableResources,
      };

      // 🚀 PROGRESSIVE GENERATION: Generate program structure with AI (no quests yet)
      const aiProgram = await aiService.generateProgramStructure(
        profile,
        goalSanitizeResult.goal,
        validatedData.duration,
        schedule,
        validatedData.intensity,
        validatedData.domain,
        validatedData.domainAnswers || {},
        validatedData.aiFollowUpAnswers,
        userContext
      );

      // Calculate dates
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + validatedData.duration);

      // Calculate total phases (weeks) - use floor for complete weeks
      // 30 days = 4 weeks (not 5), 21 days = 3 weeks, etc.
      const totalPhases = Math.floor(validatedData.duration / 7);

      // Create milestones with IDs and dates
      const milestones = aiProgram.milestones.map((m, index) => {
        const targetDate = new Date(startDate);
        targetDate.setDate(targetDate.getDate() + (m.phase * 7));
        
        return {
          ...m,
          id: `milestone_${Date.now()}_${index}`,
          targetDate: targetDate.toISOString().split('T')[0],
          completed: false,
          completedAt: null,
        };
      });

      // 🚀 PROGRESSIVE GENERATION: No quest templates needed - we generate quests progressively
      const questTemplates: any[] = []; // Empty array for progressive generation

      // Schedule ALL quests upfront across the entire program duration
      const programId = `program_${Date.now()}`;
      
      // Create a temporary program object for quest generation
      const tempProgram: Program = {
        id: programId,
        userId: user.uid,
        title: aiProgram.title,
        goal: goalSanitizeResult.goal,
        domain: validatedData.domain,
        domainAnswers: validatedData.domainAnswers,
        aiFollowUpAnswers: validatedData.aiFollowUpAnswers,
        duration: validatedData.duration,
        schedule,
        intensity: validatedData.intensity,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        status: 'active',
        progress: 0,
        currentPhase: 1,
        totalPhases,
        milestones,
        questTemplates,
        createdAt: Date.now(),
        pausedAt: null,
        completedAt: null,
        questsGenerated: 0,
        questsCompleted: 0,
        userContext,
        preferredTimeStart,
        preferredTimeEnd,
        availableDays,
      };

      // 🚀 FIX: Reorder availableDays to start with the program's start day
      // This ensures Quest 1 is scheduled for today, not the first day of the week
      let reorderedAvailableDays = availableDays;
      if (availableDays && availableDays.length > 0) {
        const startDayOfWeek = startDate.getDay(); // 0=Sun, 1=Mon, etc.
        // Reorder so it starts with the start day and wraps around
        reorderedAvailableDays = [...availableDays].sort((a, b) => {
          // Calculate offset from start day
          const offsetA = (a - startDayOfWeek + 7) % 7;
          const offsetB = (b - startDayOfWeek + 7) % 7;
          return offsetA - offsetB;
        });
        console.log(`🚀 Reordered availableDays from ${JSON.stringify(availableDays)} to ${JSON.stringify(reorderedAvailableDays)} (start day: ${startDayOfWeek})`);
        
        // Update the tempProgram to use reordered days
        tempProgram.availableDays = reorderedAvailableDays;
      }

      // 🚀 PROGRESSIVE GENERATION: Generate first week quests using AI
      console.log('🚀 About to generate first week quests using AI...');
      
      // Call the Cloud Function for AI-powered first week quest generation
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const generateProgressiveQuestsFunc = httpsCallable(functions, 'generateProgressiveQuests');
      
      const result = await generateProgressiveQuestsFunc({
        program: tempProgram,
        phase: 1, // First week
        userId: user.uid
      });

      const response = result.data as any;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to generate first week quests');
      }

      const { quests: firstWeekQuests } = response.data;
      console.log('🚀 AI Generated first week quests:', firstWeekQuests.length);
      
      // Calculate total quests that will be generated for AI program
      const totalQuestsExpected = programService.calculateExpectedQuestsForAIProgram(tempProgram);
      console.log(`🚀 Total quests expected for AI program: ${totalQuestsExpected}`);
      
      // Create the final program object with total quest count
      const newProgram: Program = {
        ...tempProgram,
        questsGenerated: firstWeekQuests.length, // Start with first week count
        totalQuestsExpected, // Track total expected for progress (AI programs)
        questTemplates: [], // 🚀 PROGRESSIVE: No quest templates initially
        // 🛡️ FAIL-SAFE: Track that Phase 1 generation is complete
        generationStatus: {
          phase: 1,
          status: 'completed',
          startedAt: Date.now(),
          completedAt: Date.now()
        },
        phasesGenerated: [1] // Track that Phase 1 is complete
      };

      // Add to programs
      setPrograms(prev => [...prev, newProgram]);
      
      // Save program to Firestore
      await firestoreService.saveProgram(user.uid, newProgram);
      
      // ✅ USAGE IS TRACKED SERVER-SIDE (Cloud Function already incremented it)
      // The server-side Cloud Function (generateProgramWithAI) automatically tracks usage
      // The Firestore listener will automatically update our local state with the new value
      // No need to track client-side (would be blocked by security anyway)
      
      if (firstWeekQuests.length > 0) {
        // Create first week quests with proper scheduling and subtasks
        const questsToCreate = firstWeekQuests.map((q: any) => {
          // Calculate scheduled date based on dayOfPhase and availableDays for Week 1
          let scheduledDate: Date;

          if (newProgram.availableDays && newProgram.availableDays.length > 0) {
            // AI should generate dayOfPhase as sequential numbers (1, 2, 3, ...)
            // Map them to the corresponding available day from the array (now reordered to start with program start day)
            const dayIndex = q.dayOfPhase - 1; // Convert to 0-based index
            const targetDayOfWeek = newProgram.availableDays[dayIndex];

            console.log(`🚀 Week 1 Scheduling debug - dayOfPhase: ${q.dayOfPhase}, dayIndex: ${dayIndex}, targetDayOfWeek: ${targetDayOfWeek}`);
            console.log(`🚀 Week 1 Available days array: ${JSON.stringify(newProgram.availableDays)} - Mapping: 1→${newProgram.availableDays[0]}, 2→${newProgram.availableDays[1]}, 3→${newProgram.availableDays[2]}`);

            if (targetDayOfWeek !== undefined) {
              // Find the next occurrence of this day of the week from program start
              const programStartDate = new Date(newProgram.startDate);
              const startDayOfWeek = programStartDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

              // Calculate days to add to reach the target day
              let daysToAdd = targetDayOfWeek - startDayOfWeek;
              if (daysToAdd < 0) {
                daysToAdd += 7; // Wrap to next week
              }

              scheduledDate = new Date(programStartDate);
              scheduledDate.setDate(scheduledDate.getDate() + daysToAdd);

              console.log(`🚀 Scheduling Week 1 Quest: ${q.title} for available day ${targetDayOfWeek} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][targetDayOfWeek]}), daysToAdd: ${daysToAdd}, finalDate: ${scheduledDate.toDateString()}`);
            } else {
              // Fallback to sequential if something goes wrong
              scheduledDate = new Date(newProgram.startDate);
              scheduledDate.setDate(scheduledDate.getDate() + (q.dayOfPhase - 1));
              console.log(`🚀 Scheduling Week 1 Quest (fallback): ${q.title} for day ${q.dayOfPhase}`);
            }
          } else {
            // Fallback: no availableDays specified, use sequential
            scheduledDate = new Date(newProgram.startDate);
            scheduledDate.setDate(scheduledDate.getDate() + (q.dayOfPhase - 1));
            console.log(`🚀 Scheduling Week 1 Quest (no available days): ${q.title} for day ${q.dayOfPhase}`);
          }

          const scheduledFor = scheduledDate.toISOString().split('T')[0];

          return {
            title: `[${newProgram.title.split(' ')[0]}] ${q.title}`,
            description: q.description,
            type: q.type,
            difficulty: q.difficulty,
            resetDaily: false,
            scheduledFor: scheduledFor,
            scheduledTime: preferredTimeStart && preferredTimeEnd ? {
              start: preferredTimeStart,
              end: preferredTimeEnd
            } : undefined,
            allDay: false,
            programId: newProgram.id, // Link quest to program
            resources: q.resources || [],
            subTasks: q.subTasks?.map((taskText: string, index: number) => ({
              id: `subtask_1_${q.dayOfPhase}_${index}`,
              text: taskText,
              completed: false,
              completedAt: null
            })) || [],
          };
        });

        console.log('🚀 AI First week quest creation data:', questsToCreate.map((q: any) => ({
          title: q.title,
          scheduledFor: q.scheduledFor,
          subTasks: q.subTasks?.length || 0,
          subTasksContent: q.subTasks?.map((st: any) => st.text) || []
        })));

        // 🔓 Bypass rate limiting for AI-generated program quests (trusted operation)
        await createQuestsBatch(questsToCreate, true);
        
        // Calculate how many are for today
        const todayStr = new Date().toISOString().split('T')[0];
        const todayQuestsCount = questsToCreate.filter((q: any) => q.scheduledFor === todayStr).length;
        
        // Show notification with remaining count
        const remainingText = limitCheck.remaining === 'unlimited' 
          ? 'Unlimited programs' 
          : `${Math.max(0, (limitCheck.remaining as number) - 1)} program generations`;
        showNotification(
          `✨ Program created: ${newProgram.title}! (${firstWeekQuests.length} quests for Week 1, ${todayQuestsCount} available today) 🎉\n${remainingText} remaining this month.`,
          'achievement'
        );
      } else {
        showNotification(`✨ Program created: ${newProgram.title}!`, 'success');
      }

      soundService.trainingProgramCreated();

      // Return the program ID for the caller
      return newProgram.id;
    } catch (error: any) {
      console.error('Program creation error:', error);
      showNotification(`❌ Failed to create program: ${error.message}`, 'warning');
      throw error; // Re-throw so caller can handle
    } finally {
      setShowProgramLoading(false);
    }
  };

  const pauseProgram = async (programId: string) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    const updatedProgram = programService.pauseProgram(program);
    setPrograms(prev => prev.map(p => p.id === programId ? updatedProgram : p));
    
    // Save to Firestore
    if (user) {
      await firestoreService.saveProgram(user.uid, updatedProgram);
    }
    
    showNotification(`⏸ Program paused: ${program.title}`, 'warning');
  };

  const resumeProgram = async (programId: string) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    const updatedProgram = programService.resumeProgram(program);
    setPrograms(prev => prev.map(p => p.id === programId ? updatedProgram : p));
    
    // Save to Firestore
    if (user) {
      await firestoreService.saveProgram(user.uid, updatedProgram);
    }
    
    soundService.notification();
    showNotification(`▶ Program resumed: ${program.title}`, 'success');
  };

  const deleteProgram = async (programId: string) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    try {
      // Delete all quests associated with this program using the programId field
      const programQuests = quests.filter(q => q.programId === programId);
      const questIds = programQuests.map(q => q.id);
      
      // ⚡ Use bulk delete for efficiency (single rate limit check, single batch operation)
      if (questIds.length > 0) {
        await firestoreService.deleteBulkQuests(questIds);
      }
      
      // Remove quests from local state
      setQuests(prev => prev.filter(q => q.programId !== programId));
      
      // Delete program from Firestore
      await firestoreService.deleteProgram(programId);
      
      // Remove program from local state
      setPrograms(prev => prev.filter(p => p.id !== programId));
      
      soundService.notification();
      showNotification(`🗑️ Program deleted: ${program.title} (${questIds.length} quests removed)`, 'success');
    } catch (error) {
      console.error('Error deleting program:', error);
      showNotification('❌ Failed to delete program', 'warning');
    }
  };

  const signOut = async () => {
    try {
      // Force sync any pending changes before signing out
      await firestoreSyncService.forceSync();
      
      await authService.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get effective stats with passive boosts applied
  const getEffectiveStats = (): Stats => {
    if (!profile) {
      return {
        strength: 0,
        intelligence: 0,
        agility: 0,
        vitality: 0,
        sense: 0,
      };
    }

    const baseStats = profile.stats;
    const boosts = profile.passiveStatBoosts;

    // Apply percentage boosts
    return {
      strength: Math.floor(baseStats.strength * (1 + (boosts.strength || 0))),
      intelligence: Math.floor(baseStats.intelligence * (1 + (boosts.intelligence || 0))),
      agility: Math.floor(baseStats.agility * (1 + (boosts.agility || 0))),
      vitality: Math.floor(baseStats.vitality * (1 + (boosts.vitality || 0))),
      sense: Math.floor(baseStats.sense * (1 + (boosts.sense || 0))),
    };
  };

  // ⚡ OPTIMIZATION: Passive skill context functions
  const updatePassiveSkillInContext = (skillId: string, updates: any) => {
    setPassiveSkills(prev => prev.map(skill => 
      skill.id === skillId ? { ...skill, ...updates } : skill
    ));
    
    // In demo mode, also update session storage
    if (isDemoMode) {
      const demoUserStr = sessionStorage.getItem('demo_user');
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr);
        const updatedPassiveSkills = (demoUser.passiveSkills || []).map((skill: any) => 
          skill.id === skillId ? { ...skill, ...updates } : skill
        );
        const updatedDemoUser = {
          ...demoUser,
          passiveSkills: updatedPassiveSkills
        };
        sessionStorage.setItem('demo_user', JSON.stringify(updatedDemoUser));
      }
    }
  };

  const addPassiveSkillToContext = (skill: any) => {
    setPassiveSkills(prev => [...prev, skill]);
    
    // In demo mode, also update session storage
    if (isDemoMode) {
      const demoUserStr = sessionStorage.getItem('demo_user');
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr);
        const updatedDemoUser = {
          ...demoUser,
          passiveSkills: [...(demoUser.passiveSkills || []), skill]
        };
        sessionStorage.setItem('demo_user', JSON.stringify(updatedDemoUser));
      }
    }
  };

  const removePassiveSkillFromContext = (skillId: string) => {
    setPassiveSkills(prev => prev.filter(skill => skill.id !== skillId));
    
    // In demo mode, also update session storage
    if (isDemoMode) {
      const demoUserStr = sessionStorage.getItem('demo_user');
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr);
        const updatedPassiveSkills = (demoUser.passiveSkills || []).filter((skill: any) => skill.id !== skillId);
        const updatedDemoUser = {
          ...demoUser,
          passiveSkills: updatedPassiveSkills
        };
        sessionStorage.setItem('demo_user', JSON.stringify(updatedDemoUser));
      }
    }
  };

  // Background Settings Management
  const updateBackgroundSettings = async (settings: BackgroundSettings) => {
    if (!profile) throw new Error('Profile not loaded');

    try {
      if (isDemoMode) {
        // Save to localStorage for demo mode
        localStorage.setItem('demo_background_settings', JSON.stringify(settings));
      } else {
        // Save to Firestore for real users
        if (user) {
          await saveBackgroundSettings(user.uid, settings);
        }
      }

      // Update local state
      setProfile({
        ...profile,
        backgroundSettings: settings,
      });

      showNotification('Background settings saved!', 'success');
    } catch (error) {
      console.error('Error updating background settings:', error);
      showNotification('Failed to save background settings', 'warning');
      throw error;
    }
  };

  const getBackgroundSettings = (): BackgroundSettings => {
    if (isDemoMode) {
      // Load from localStorage for demo mode
      const demoSettings = localStorage.getItem('demo_background_settings');
      if (demoSettings) {
        try {
          const parsed = JSON.parse(demoSettings);
          return cleanBackgroundSettings(parsed);
        } catch (error) {
          console.error('Error parsing demo background settings:', error);
          return getDefaultBackgroundSettings();
        }
      }
      return getDefaultBackgroundSettings();
    }
    
    if (profile?.backgroundSettings) {
      // Clean stale data from old background types
      const cleaned = cleanBackgroundSettings(profile.backgroundSettings);
      
      // If settings were cleaned (stale data removed), save back to DB
      if (JSON.stringify(cleaned) !== JSON.stringify(profile.backgroundSettings)) {
        if (user) {
          saveBackgroundSettings(user.uid, cleaned).catch(err => 
            handleError(err, 'Failed to auto-clean background settings', true)
          );
        }
        // Update local state immediately
        setProfile({
          ...profile,
          backgroundSettings: cleaned,
        });
      }
      
      return cleaned;
    }
    return getDefaultBackgroundSettings();
  };

  return (
    <UserContext.Provider
      value={{
        profile,
        quests,
        programs,
        levelUpQueue,
        achievements,
        achievementQueue,
        shopItems,
        user: user || null,
        questCompleteData,
        clearQuestComplete,
        notifications,
        showNotification,
        removeNotification,
        showPenaltyModal,
        failedDailyQuests,
        acceptPenalty,
        dailyReward,
        showDailyRewardModal,
        claimDailyReward,
        allocateStatPoints,
        refreshProfile,
        refreshQuests,
        updateProfile,
        completeQuest,
        createQuest,
        createQuestsBatch,
        updateQuest,
        toggleSubTask,
    deleteQuest,
    rescheduleQuest,
    clearLevelUpQueue,
    setLevelUpQueue,
        clearAchievementQueue,
        purchaseItem,
        completePomodoroSession,
        createProgram,
        pauseProgram,
        resumeProgram,
        deleteProgram,
        getEffectiveStats,
        // ⚡ OPTIMIZATION: Passive skills in context
        passiveSkills,
        updatePassiveSkillInContext,
        addPassiveSkillToContext,
        removePassiveSkillFromContext,
        updateBackgroundSettings,
        getBackgroundSettings,
        signOut,
        isDemoMode,
      }}
    >
      {profileCreationInProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg text-center">
            <div className="text-4xl mb-4 animate-spin">⚔️</div>
            <div className="text-white font-orbitron">Creating your profile...</div>
            <div className="text-sm text-gray-400 mt-2">Please wait while we set up your account</div>
          </div>
        </div>
      )}
      {children}
      <ProgramLoadingModal isOpen={showProgramLoading} programDuration={currentProgramDuration} />
      
      {/* Demo Banner */}
      {isDemoMode && (
        <DemoBanner
          onSignUp={() => {
            setIsDemoMode(false);
            window.location.href = '/auth';
          }}
          onSignIn={() => {
            setIsDemoMode(false);
            window.location.href = '/auth';
          }}
        />
      )}
      
      {/* Demo Conversion Modal */}
      <DemoConversionModal
        isOpen={showDemoConversion}
        onClose={() => setShowDemoConversion(false)}
        onSignUp={() => {
          setIsDemoMode(false);
          setShowDemoConversion(false);
          window.location.href = '/auth';
        }}
        onSignIn={() => {
          setIsDemoMode(false);
          setShowDemoConversion(false);
          window.location.href = '/auth';
        }}
      />
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

